// Hook que maneja el chat con el agente para Interfaz V2.
//
// Patrón cloneado del Director de Estudio (VideoEditor.tsx ~9977+):
//   1. Cliente arma `apiMessages` con el historial.
//   2. POST a `/functions/v1/chat-agent` con `{ messages, tools, system, project_id, id_company }`.
//   3. Parsea el stream SSE bloque por bloque (separador `\n\n`).
//   4. Si el modelo invoca tools, ejecuta cada una. Las tools de UI devuelven
//      `Promise` que bloquea hasta que el usuario responde.
//   5. Loop de hasta MAX_TOOL_ROUNDS rondas hasta que el modelo cierra el turno.
//
// La diferencia clave con el Director:
//   • No persiste en Supabase (chat in-memory, se resetea al cerrar).
//   • System prompt + catálogo de tools propios (más chico).
//   • project_id es un UUID efímero por sesión (sessionStorage).
//   • Sin attachments por ahora (las imágenes las elige el user vía tool).

import { registroDeGeneracionParaHistorial } from './registroDeGeneracion';
import { adjuntoCorregidoSiEsVideo } from '../../chat-atlas/ui/bibliotecaDeAdjuntos';
import type { DescripcionDePantalla } from '../../chat-atlas/puenteDePantalla';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { SUPABASE_ANON_KEY, AGENT_CHAT_URL } from '../../config';
import { aligerarImagenesEnMensajes, conFirmasFrescas, refrescarFirmasEnMensajes } from '../../utils/firmasFrescas';
import { fusionarMensajes, leerSincronia, sincroniaTrasGuardar } from './chatCompartido';
import {
  ErrorDelAgente,
  ErrorEnElStream,
  esErrorDeContexto,
  MENSAJE_SIN_RONDAS,
  MENSAJE_TURNO_VACIO,
  mensajeDeErrorDelTurno,
} from './erroresDelTurno';
import {
  PREFERENCIAS_POR_DEFECTO,
  type PreferenciasDeGeneracion,
} from '../../chat-atlas/preferenciasDeGeneracion';
import type { TipoAccion } from '../../chat-atlas/confirmaciones';
import { HERRAMIENTA_BUSCAR_EN_EL_CHAT, V2_AGENT_TOOLS, type V2Tool } from './agentTools';
import { buscarEnElChat } from './buscarEnElChat';
import { fetchProductVistas, getProductFullImageUrl, type FotoDeProducto } from './productsApi';
import { compactarHistorial, type EstadoDeResumen } from './compactarApi';
import {
  aplicarCompactacion,
  avisoDeChatCompactado,
  FRACCION_COLA,
  puntoDeCorte,
  resumenDeEmergencia,
  transcripcionDelTramo,
  ventanaDelModelo,
} from './compactacionDeContexto';
import { esKindDeCard, type V2ToolCardKind } from './kindsDeCard';
import { formatearDuracion } from '../../chat-atlas/adjuntosChatAtlas';
import {
  accionHecha,
  ETIQUETA_PENSANDO,
  etiquetaAlLeerResultados,
  etiquetaConAvance,
  etiquetaDeTool,
  TOOLS_SIN_CARD,
} from './actividadDelAgente';
import { buildV2SystemPrompt } from './agentSystemPrompt';
import { bloquesDeLaPagina, entradaDeAbrirWeb, imagenesWebDe, pedirPagina } from './abrirWeb';
import type { MockProductV2 } from './mockData';
import {
  type ChatAttachment,
  signReferenceImage,
  subirFotoDelPdf,
} from './agentAttachments';
import { mencionesEn } from './mencionesDeAdjuntos';
import { MODEL_MEDIA, minCreditsForModel } from './agentModels';
import {
  fetchMemories,
  saveMemory,
  fetchBrandContext,
  type AgentMemory,
  type BrandContext,
} from './agentMemoryApi';
import { fetchSkillsDeEmpresa, guardarSkillDeEmpresa } from './skills/agentSkillsApi';
import {
  normalizarSkillNueva,
  resultadoDeCargarSkill,
  resultadoDeGuardarSkill,
  unirSkills,
  type SkillDelAgente,
} from './skills/skill';
import { EVENTO_SKILL_CARGADA, propsDeSkillCargada } from './skills/registroDeSkills';
import { trackEvent } from '../../lib/posthog';
import { NOMBRES_DE_FABRICA, SKILLS_DE_FABRICA } from './skills/skillsDeFabrica';
import { buildOutputKindHintLine } from './modes/asistente/directMode';
import { registroLoteParaHistorial, type ResumenLote } from './lotes/loteImagenes';
import {
  registroProductosParaHistorial,
  type ResumenCreacion,
} from './lotes/organizarProductos';
import { bloquesVerProductos, idsPedidos } from './lotes/verProductos';
import { consultaDe, desdeDe, limiteDe, resultadoDeBusqueda, soloConSkuDe } from './buscarProductosDelAgente';
import { indiceParaBuscar, type ResumenParaBuscar } from '../../lib/indiceDeBusquedaDeProductos';
import {
  registroGuardadoParaHistorial,
  type ItemGuardado,
  type TipoGuardado,
} from './mostrarGuardado';
import { restaurarToolCards } from './restaurarToolCards';
import { registroPreguntasParaHistorial } from './preguntas/registroPreguntas';
export { INLINE_PENDING_KINDS, isInlinePendingCard } from './restaurarToolCards';
import {
  bloquesFotosPerdidas,
  bloquesVerFotos,
  bloquesVerImagenes,
  idsDeFotosPedidas,
  ventanaDeFotos,
} from './lotes/verFotos';
import { resolverImagenes } from './resolverImagenPorId';
import { fetchUserModels, type ModelV2 } from './modelsApi';
import { fondoGuardadoPorId, poseGuardadaPorId } from './referenciasDelCatalogo';
import { fotosDelLote } from './lotes/lotesDeFotos';
import { urlDeFotoDelLote, urlsDeFotosDelLote } from './lotes/subirFotosDelLote';
import {
  resumirAdjuntos,
  documentosEnMemoriaDe,
  elegirPdfAdjuntado,
  elegirPlanillaAdjuntada,
  adjuntoPorId,
  ultimoLoteAdjuntado,
  type AdjuntoDeMensaje,
  type ImagenDeLaWeb,
  elegirVideoAdjuntado,
  marcadorDeAdjuntos,
} from './adjuntosDelMensaje';
import {
  encabezadoDeImagenes,
  lecturaDePdf,
  paginasPedidas,
  registroPdfParaHistorial,
  textoDeFotosDelPdf,
  ventanaDePaginas,
  type FotoDelPdfSubida,
  type PdfCreado,
} from './pdfs/pdf';
import { liberarPdf, obtenerPdf } from './pdfs/pdfsEnMemoria';
import { resumenDeLanzamiento } from '../lanzamientos/agenteLanzamiento';
import { destinosDe, nombresDeDestinos } from '../lanzamientos/canales';
import { fetchLanzamiento, lanzamientoParaElAgente } from '../lanzamientos/lanzamientosApi';
import {
  lecturaDePlanilla,
  registroPlanillaParaHistorial,
  ventanaDeFilas,
  type PlanillaCreada,
} from './planillas/planilla';
import { liberarPlanilla, obtenerPlanilla } from './planillas/planillasEnMemoria';
import { etiquetaDeReferenciaParaElMotor, tipoDeReferenciaParaElMotor, type ReferenciaEtiquetada } from './referenciasDeGeneracion';
import { lineaDeContextoDeMoodboards } from '../../chat-atlas/verMoodboard';
import { notaDeSistemaParaElUsuario, sinRegistrosDelModelo } from './marcadoresDelSistema';
import { registroDeHerramienta } from './registrosDeHerramientas';
import { avisoDeVideoDeReferencia } from '../../utils/videoDeReferencia';
import { formatosPersonalizadosActuales } from '../../utils/formatosPersonalizadosStore';
import type { FormatoPersonalizado } from '../../utils/formatoPersonalizado';
import {
  imageIdDeCreacion,
  lineasDeReferenciasAdjuntas,
  modelIdDeAdjunto,
} from './adjuntosDelMensaje';

/** Acota un valor a `n` chars (colapsando whitespace) para los [registro:]
 *  del historial de API — los prompts largos no deben inflar cada turno. */
const clipForLog = (v: unknown, n: number): string => {
  const s = String(v ?? '')
    .replace(/\s+/g, ' ')
    .trim();
  return s.length > n ? `${s.slice(0, n)}…` : s;
};

// Las etiquetas de "qué está haciendo" (server-side Y del cliente, con avance) viven en
// `actividadDelAgente.ts` — ADR-2026-09-09-el-chat-dice-que-esta-haciendo.

// ─── Tipos ────────────────────────────────────────────────────────────

export type V2ChatRole = 'user' | 'assistant';

/** Adjunto tal como se muestra en la burbuja del usuario, ya enviado. Es una
 *  copia LIVIANA del `ChatAttachment` (miniatura + nombre), no el adjunto
 *  vivo: los chips del composer se vacían al enviar, y el mensaje tiene que
 *  seguir mostrando con qué se mandó. */
export interface V2MessageAttachment {
  kind: 'image' | 'product' | 'model';
  /** Miniatura. Puede vencer (URL firmada) o no existir: la burbuja cae a un
   *  placeholder, nunca rompe. */
  previewUrl?: string;
  name?: string;
}

export interface V2ChatMessage {
  role: V2ChatRole;
  text: string;
  /** Lo que el usuario enganchó con el "+" en ESTE mensaje. Sólo en burbujas
   *  de user. */
  attachments?: V2MessageAttachment[];
  /** Tool UI cards anchored a este mensaje del agente, en orden. */
  toolCards?: V2ToolCard[];
  /** Si es 'upgrade', el mensaje se renderiza como un container de "mejorar
   *  plan" (mismo modal de créditos agotados de toda la app), en vez de texto. */
  kind?: 'upgrade';
  /**
   * Respuestas gráficas que el agente colgó de este mensaje (herramienta silenciosa
   * `mostrar_respuesta_grafica` del Chat — ADR-2026-09-08-el-chat-toma-el-motor-del-asistente).
   * El `html` lo escribe un modelo: sólo se dibuja en `VisorGrafico`, dentro de un
   * iframe aislado. Al historial de la API viaja un marcador con el título, no el HTML.
   */
  graficos?: Array<{ id: string; titulo: string; html: string }>;
  /** Continuaciones sugeridas (herramienta silenciosa `sugerir_acciones` del Chat):
   *  chips debajo de la respuesta, sin efecto hasta que se tocan. */
  sugerencias?: string[];
  /** Un botón para desbloquear algo que el plan no incluye (hoy: los videos del Chat —
   *  ADR-2026-10-12-el-agente-es-de-todos). Lo cuelga una herramienta silenciosa. */
  desbloquear?: 'videos' | 'creditos';
  /** El botón «Ver en el calendario»: lo cuelga `mandar_a_instagram` cuando armó un post
   *  (ADR-2026-10-13-mandar-a-instagram-desde-el-chat). */
  verCalendario?: boolean;
  /**
   * Lo que el usuario adjuntó a ESTE mensaje (sólo en `role: 'user'`) — para dibujarlo en
   * su burbuja y para que `agregar_productos` encuentre las fotos después
   * (ADR-2026-09-08-agregar-productos-es-una-herramienta). Sin URLs firmadas.
   */
  adjuntos?: AdjuntoDeMensaje[];
  /**
   * Una línea por herramienta que el agente usó en este mensaje y que no deja card con
   * marcador propio (lo que miró, lo que leyó, la skill que cargó, lo que el usuario
   * eligió): con los ids del resultado, sin las imágenes. Viajan en la nota del sistema
   * del turno siguiente (ADR-2026-09-13-todo-lo-que-uso-el-agente-queda-registrado).
   */
  registros?: string[];
  /**
   * Las herramientas que usó en esta ronda, en orden: las que leen dejan una línea de lo que hizo
   * ("Buscó en tus productos"), ver `accionesDelTurno`. Para la pantalla; al modelo le llegan los
   * `registros` (ADR-2026-10-22-cada-accion-del-asistente-queda-en-una-linea).
   */
  acciones?: string[];
  /** Las imágenes que el agente trajo de una página con `abrir_web`, ya guardadas: se usan
   *  como adjuntos por su id (`web-…`) — ADR-2026-10-15-el-agente-abre-paginas-web. */
  imagenesWeb?: ImagenDeLaWeb[];
}

/**
 * Lo que el que monta el hook puede sumarle, TODO opcional — el asistente no pasa nada y
 * sigue igual; el Chat ("Empleado IA") pasa lo suyo
 * (ADR-2026-09-08-el-chat-toma-el-motor-del-asistente). Las funciones se leen en cada
 * envío por ref, así el que llama puede devolver el estado vigente sin recrear el hook.
 */
export interface OpcionesV2Chat {
  /**
   * Prefijo de las claves de `sessionStorage` (mensajes, chat actual, flag de
   * investigación). Sin prefijo son las de siempre; el Chat usa las suyas para no pisar
   * la sesión del asistente cuando los dos están montados.
   */
  prefijoStorage?: string;
  /** Herramientas que se suman a `V2_AGENT_TOOLS` en el pedido de este envío. */
  herramientasExtra?: () => V2Tool[];
  /**
   * Herramientas que se resuelven SOLAS, sin tarjeta ni turno del usuario: el handler
   * devuelve el `tool_result` y puede colgar cosas del último mensaje del asistente
   * (gráficos, sugerencias). Se consultan por nombre ANTES de las del catálogo.
   */
  herramientasSilenciosas?: Record<string, HandlerSilencioso>;
  /**
   * Resuelve por nombre una herramienta de PANTALLA: las que el editor de video registra
   * en el puente mientras está montado (ADR-2026-09-11-el-director-del-estudio-se-funde-
   * en-el-agente). Se consulta después de `herramientasSilenciosas` y antes del catálogo;
   * null si el nombre no es de la pantalla actual. Se lee en cada tool call, así una
   * pantalla que se desmontó a mitad de turno deja de resolver.
   */
  resolverHerramienta?: (nombre: string) => HandlerSilencioso | null;
  /** Etiqueta de actividad de una herramienta que el motor no conoce (las de pantalla);
   *  null para las de siempre, que tienen la suya en `actividadDelAgente`. */
  etiquetaDeHerramienta?: (nombre: string) => string | null;
  /** En qué pantalla está el usuario (la ruta). Viaja al backend sólo para la métrica del
   *  turno: `modo_utilizado` ya no distingue "editor" de "chat" desde que son lo mismo. */
  pantalla?: () => string | null;
  /**
   * El proyecto del Estudio de Video en el que está el usuario, si está en uno. Se lee
   * al CREAR la fila en `agent_chats`: el chat queda asociado a ese proyecto y abrir el
   * chat en ese editor lo vuelve a traer (ADR-2026-09-11-el-chat-del-proyecto-de-video).
   */
  proyecto?: () => string | null;
  /**
   * La tarjeta `ir_al_estudio` ya creó el proyecto con el video: el que monta el hook
   * lleva al usuario al Estudio, deja el panel abierto y espera a que la pantalla se
   * registre; devuelve su ficha (o null si no llegó a tiempo). El motor, antes, ata este
   * chat al proyecto (ADR-2026-10-11-ir-al-estudio-desde-el-chat).
   */
  alLlegarAlEstudio?: (projectId: string) => Promise<DescripcionDePantalla | null>;
  /** `web_search` del pedido. Default `true` (el asistente siempre la manda). */
  busquedaWeb?: () => boolean;
  /**
   * Campos que se suman al cuerpo del pedido de cada turno. Hoy: `meta_ads` + `brand_id`
   * para que el backend adjunte el MCP de anuncios de Meta de esa marca
   * (ADR-2026-09-24-anuncios-de-meta-en-el-chat). Se lee en cada envío.
   */
  cuerpoExtra?: () => Record<string, unknown>;
  /** Formato y calidad por defecto para generar (menú "+" → Preferencias del Chat):
   *  van al bloque volátil del prompt y las cards los preseleccionan
   *  (ADR-2026-09-13-preferencias-de-generacion-en-el-menu-del-mas). */
  preferencias?: () => PreferenciasDeGeneracion;
  /**
   * Guardar una preferencia. Lo llaman las cards cuando el usuario CAMBIA el formato o la
   * calidad ahí mismo: lo que elige en la card pasa a ser su default
   * (ADR-2026-09-14-cambiar-el-formato-en-la-card-cambia-el-default). Ausente en el
   * asistente legacy, que no tiene pantalla de Preferencias: ahí no pasa nada.
   */
  recordarPreferencia?: <K extends keyof PreferenciasDeGeneracion>(
    clave: K,
    valor: PreferenciasDeGeneracion[K],
  ) => void;
  /** Texto que se suma al bloque estable y al volátil del system prompt. */
  promptExtra?: () => { estable?: string; volatil?: string };
  /** Se espera antes de armar el prompt de cada envío: lo usa el Chat para leer el saldo
   *  fresco (ADR-2026-10-12-sin-creditos-en-el-chat). Si falla, el turno sigue igual. */
  antesDelTurno?: () => Promise<void>;
  /**
   * ¿Esta tool se resuelve SOLA, sin esperar el click del usuario? La lee el motor al
   * crear la card. Sin la opción no se resuelve sola ninguna, que es como funcionó siempre
   * (ADR-2026-09-08-permisos-de-generacion-del-chat,
   * ADR-2026-09-08-permisos-tambien-para-el-catalogo).
   */
  sinConfirmacion?: (tool: string) => boolean;
  /**
   * El botón blanco de la card: «… sin preguntarme». Deja el permiso puesto para las
   * próximas veces (ADR-2026-09-13-generar-sin-preguntarme-en-la-card).
   *
   * Viene por acá —y no del contexto del Chat— porque `ChatPanelV2` también se monta en
   * el `InterfazV2` legacy, donde `useChatAtlas` tira. Sin esta opción el botón no se
   * dibuja, que es lo correcto ahí: ese asistente no tiene permisos que cambiar.
   */
  noPreguntarMas?: (tipo: TipoAccion) => void;
  /**
   * Líneas que se suman al bloque `[CONTEXTO ADJUNTO POR EL USUARIO]` del mensaje que se
   * está por mandar — el Chat avisa así de un lote de fotos enganchado
   * (ADR-2026-09-08-el-chat-trabaja-en-lote). Se lee en cada envío.
   */
  contextoAdjunto?: () => string[];
  /** Adjuntos que el que monta el hook suma al RESUMEN del mensaje del usuario (el chip
   *  del lote de fotos del Chat), además de los chips del motor. */
  adjuntosDelMensaje?: () => AdjuntoDeMensaje[];
}

/**
 * Lo que devuelve una herramienta silenciosa. Un objeto se serializa a JSON; un string va
 * tal cual; `{ __bloques }` manda bloques de contenido (texto + imágenes); `{ __texto,
 * __error: true }` es un `tool_result` con `is_error` (el modelo lee el motivo y sigue).
 */
export type ResultadoSilencioso = string | Record<string, unknown>;

export type HandlerSilencioso = (
  input: unknown,
  ctx: { adjuntarAlUltimoAssistant: (patch: Partial<V2ChatMessage>) => void },
) => ResultadoSilencioso | Promise<ResultadoSilencioso>;

/** Las claves de `sessionStorage` del hook, con el prefijo del que lo monta. */
export interface ClavesStorageV2Chat {
  mensajes: string;
  chatId: string;
  research: string;
  /** El resumen vigente de la parte vieja del chat, si ya se compactó
   *  (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar). Sin esto, una recarga
   *  volvía a mandar el historial entero y a pagar otro resumen. */
  resumen: string;
  /** Versión y largo sincronizado del chat en pantalla: sin esto, una recarga guardaba sin
   *  condición y podía borrar lo que un compañero sumó a un chat compartido
   *  (ADR-2026-10-13-review-176-el-chat-compartido-no-pisa-al-companero). */
  sincronia: string;
}

/** Sin prefijo, las claves históricas del asistente; con prefijo, un juego aparte. */
export function clavesDeStorage(prefijo?: string): ClavesStorageV2Chat {
  const p = prefijo ? `${prefijo}-` : 'atlas-v2-';
  return {
    mensajes: `${p}chat-messages`,
    chatId: `${p}current-chat-id`,
    research: `${p}research-enabled`,
    resumen: `${p}chat-resumen`,
    sincronia: `${p}chat-sincronia`,
  };
}

/** La lista (y el tipo) viven en `kindsDeCard.ts`: `executeTool` la necesita en tiempo de
 *  ejecución para no fabricar cards que nadie sabe dibujar. Se re-exporta el tipo porque
 *  media app lo importa de acá. */
export type { V2ToolCardKind };

/** Las tools cuya card puede actuar sola: el motor les estampa `autogenerate` según el
 *  permiso y la card lo obedece. `generate_audio` NO está — su `autogenerate` sí es del
 *  modelo (lo pide el schema) y es lo que hace que una locución no pida un click de más.
 *  `activar_investigacion` tampoco: no tiene card que autoconfirmar, se resuelve entera
 *  sin mostrarla. El flag NO sobrevive a una recarga: ver `restaurarToolCards`. */
const TOOLS_AUTOGENERABLES = [
  'generate_image',
  'generate_video',
  'generar_lote_imagenes',
  'agregar_productos',
];

/**
 * Una tool card es lo que el agente muestra inline. `status` controla si la
 * card está esperando input (`pending`) o ya fue respondida (`resolved`, queda
 * visible como resumen).
 */
export interface V2ToolCard {
  /** id único (= tool_use_id del evento del stream) */
  id: string;
  kind: V2ToolCardKind;
  input: any;
  status: 'pending' | 'resolved' | 'cancelled';
  /** Respuesta que se mandó de vuelta al agente — útil para renderizar el resumen. */
  response?: any;
  /** La card quedó SUELTA: el turno que la creó se soltó porque el usuario
   *  escribió otra cosa. Sigue 'pending' y clickeable (genera igual), pero ya
   *  no alimenta al agente y NO cuenta como `activeToolCard` — si contara,
   *  bloquearía para siempre a todas las cards que vengan después. */
  detached?: boolean;
}

// ─── UUID efímero por sesión ──────────────────────────────────────────
// La edge function `chat-agent` exige `project_id` (es FK en agent_messages).
// Como esta pantalla no tiene proyecto real, generamos uno por sesión y lo
// guardamos en sessionStorage para que sobreviva refreshes pero no entre
// pestañas. Cuando se valide V2, esto se reemplaza por un proyecto real.
// ─── Persistencia del chat ────────────────────────────────────────────
// Dos capas: sessionStorage = caché instantáneo (restaura al navegar sin
// round-trip) + tabla `agent_chats` (Supabase) = store durable + historial.
// Las claves salen de `clavesDeStorage` (con el prefijo del que monta el hook): la de
// mensajes, la del id del chat actual en `agent_chats` —para reconectar con la misma
// fila al volver de otra pantalla— y la del flag de investigación.
// El historial usa `clavesDeStorage().chatId` para saber si el chat que se está
// borrando es el que está en pantalla (ADR-2026-09-08-borrar-el-historial-de-chats):
// sin prefijo da el mismo string que antes exportaba `CHAT_ID_KEY`.

const loadCurrentChatId = (claves: ClavesStorageV2Chat): string | null => {
  try {
    return sessionStorage.getItem(claves.chatId);
  } catch {
    return null;
  }
};

// Flag de "investigación externa activada" (gateway tool `activar_investigacion`).
// Cuando el agente la llama, prendemos esto y a partir de ahí mandamos
// `research:true` → el edge function adjunta web_search + Apify MCP. Persistido en
// sessionStorage para sobrevivir un refresh del MISMO chat (se limpia al resetear o
// abrir otro chat del historial).
const loadResearchEnabled = (claves: ClavesStorageV2Chat): boolean => {
  try {
    return sessionStorage.getItem(claves.research) === '1';
  } catch {
    return false;
  }
};

const loadPersistedMessages = (claves: ClavesStorageV2Chat): V2ChatMessage[] => {
  try {
    const raw = sessionStorage.getItem(claves.mensajes);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return (parsed as V2ChatMessage[]).map((m) => ({
      ...m,
      toolCards: restaurarToolCards(m.toolCards),
    }));
  } catch {
    return [];
  }
};

const loadPersistedResumen = (claves: ClavesStorageV2Chat): EstadoDeResumen | null => {
  try {
    const raw = sessionStorage.getItem(claves.resumen);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EstadoDeResumen>;
    if (typeof parsed?.resumen !== 'string' || typeof parsed?.hastaMensaje !== 'number') return null;
    return {
      hastaMensaje: parsed.hastaMensaje,
      resumen: parsed.resumen,
      mensajesResumidos: parsed.mensajesResumidos ?? parsed.hastaMensaje,
    };
  } catch {
    return null;
  }
};

// Corre una promesa con un tope de tiempo. Si se pasa, RECHAZA con un error
// etiquetado (en vez de colgarse para siempre). Se usa en el setup de
// sendMessage (getSession, creación del chat, cargas de contexto): un cuelgue
// ahí dejaba el asistente en "Pensando" indefinido porque pasa ANTES del
// watchdog del fetch. El label dice QUÉ llamada falló (diagnóstico). Ver
// ADR-141.
function withTimeout<T>(p: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(
      () => reject(new Error(`timeout:${label} (${ms}ms)`)),
      ms,
    );
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

const getEphemeralProjectId = (): string => {
  const KEY = 'atlas-v2-ephemeral-project-id';
  try {
    const existing = sessionStorage.getItem(KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
};

// ─── Hook ─────────────────────────────────────────────────────────────

/** Cuánto se espera a `antesDelTurno` antes de mandar el turno sin lo que prepara. */
const TOPE_ANTES_DEL_TURNO_MS = 2500;

export interface UseV2ChatReturn {
  messages: V2ChatMessage[];
  streaming: boolean;
  /** Label de la herramienta server-side en uso (web search / scraper de
   *  Apify) mientras el agente la ejecuta. null si no hay ninguna. Se usa
   *  para mostrar un indicador tipo "Pensando…" pero con el contexto real. */
  toolActivity: string | null;
  /** Card activa esperando respuesta del user (la primera pending). */
  activeToolCard: V2ToolCard | null;
  /** Total de cards en el batch actual (el último mensaje del agente). */
  batchSize: number;
  /** 1-indexed posición de activeToolCard dentro del batch actual. */
  activeIndex: number;
  sendMessage: (text: string) => void;
  // ── Adjuntos (menú "+") ───────────────────────────────────────────
  /** Adjuntos pendientes (chips arriba del input) que se mandan con el
   *  próximo mensaje: imagen de referencia, producto o modelo. */
  attachments: ChatAttachment[];
  addAttachment: (a: ChatAttachment) => void;
  removeAttachment: (id: string) => void;
  clearAttachments: () => void;
  /** Reemplaza los chips escribiendo también el ref espejo, para poder mandar en el
   *  MISMO tick (la cola de mensajes — ADR-2026-09-13-la-cola-lleva-sus-adjuntos). */
  reemplazarAdjuntos: (lista: ChatAttachment[]) => void;
  /** URLs firmadas de las imágenes de referencia que acompañaron el último
   *  mensaje enviado — la card de generación libre las pasa como
   *  reference_images. Vacío si el último envío no tenía imágenes. */
  getReferenceImages: () => string[];
  /**
   * Lo mismo, pero con la ETIQUETA de qué es cada una (foto adjunta, pose, fondo) — para
   * que quien arma la generación pueda recortar por prioridad y no por orden de llegada
   * (ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad).
   */
  getReferenciasEtiquetadas: () => ReferenciaEtiquetada[];
  /** Formato y calidad por defecto para generar (ver `OpcionesV2Chat.preferencias`). */
  preferenciasDeGeneracion: () => PreferenciasDeGeneracion;
  /** Deja como DEFAULT lo que el usuario acaba de elegir en una card. No-op donde no hay
   *  preferencias (ver `OpcionesV2Chat.recordarPreferencia`). */
  recordarPreferencia: <K extends keyof PreferenciasDeGeneracion>(
    clave: K,
    valor: PreferenciasDeGeneracion[K],
  ) => void;
  /** Ver `OpcionesV2Chat.noPreguntarMas`. `undefined` ⇒ sin botón. */
  noPreguntarMas?: (tipo: TipoAccion) => void;
  /** Setea las referencias firmadas a mano — para el CAMINO DIRECTO del modo
   *  "Crear desde cero", que no pasa por `sendMessage` y por lo tanto no
   *  puebla `referenceImagesRef` solo. */
  setReferenceImages: (urls: string[]) => void;
  /** Pista del toggle Imagen/Video de "Crear desde cero" para el próximo
   *  envío, o `null` para no mandar ninguna (el agente decide solo). Viaja
   *  como contexto invisible dentro del mensaje del usuario. */
  setOutputKindHint: (kind: 'imagen' | 'video' | null) => void;
  /** Setea el modelo a usar en el próximo envío (según el nivel de
   *  "inteligencia" elegido). El edge function valida contra su allow-list. */
  setModel: (model: string) => void;
  /**
   * Prende/apaga la investigación externa (scrapers de Apify) desde afuera — el
   * interruptor del Chat, donde el usuario ya optó y no hace falta la tarjeta de
   * confirmación. El agente sigue pudiendo pedirla con `activar_investigacion`.
   */
  setResearchEnabled: (on: boolean) => void;
  /** Llamar cuando el user responde una tool UI. */
  resolveToolCard: (cardId: string, response: any) => void;
  /** Patchea una card existente (input/response/status). Usado por la card
   *  de resumen para EDITAR selecciones previas (producto/modelo/pose) sin
   *  volver a pegarle al agente. */
  patchToolCard: (cardId: string, patch: Partial<V2ToolCard>) => void;
  /** El id del chat actual en `agent_chats`, o null si todavía no se creó la fila. */
  getChatId: () => string | null;
  /**
   * El usuario tocó «Abrir en el Estudio» y el proyecto ya existe: ata este chat al
   * proyecto (para que abrir el chat ahí lo vuelva a traer) y pasa a `alLlegarAlEstudio`
   * del que monta el hook, que navega y devuelve la ficha de la pantalla. Sin esa opción
   * (el asistente legacy) no navega y devuelve null.
   */
  llegarAlEstudio: (projectId: string) => Promise<DescripcionDePantalla | null>;
  /**
   * Cambia SÓLO cuando se pasa a otra conversación (abrir una del historial o empezar
   * una nueva), no con cada turno. Lo que tarda y después actúa sobre "el chat actual"
   * —la tarjeta de ir al Estudio, que copia el video antes de atar el chat al proyecto—
   * lo captura al empezar y no sigue si cambió (review #171,
   * ADR-2026-10-12-review-171-estudio-y-saldo).
   */
  sesionDeChat: () => number;
  /** Carga un chat existente (desde `agent_chats`) y lo deja como el chat
   *  actual — para "continuar chat anterior" o abrir uno del historial. */
  loadChatById: (chatId: string) => Promise<void>;
  /** Suma créditos (de imágenes generadas) al tally del chat actual. */
  addChatCredits: (credits: number) => void;
  /** Lee los créditos totales consumidos en el chat actual (agente +
   *  imágenes). 0 si todavía no hay chat persistido. */
  fetchConsumo: () => Promise<number>;
  /** Corta el turno en curso (botón Detener): aborta el stream, cancela las
   *  cards pendientes y libera el input — sin borrar el chat. */
  stopTurn: () => void;
  /** Suelta el turno SIN cancelar las cards pendientes: quedan en el chat,
   *  todavía clickeables. Se usa cuando el usuario escribe otra cosa en vez
   *  de responder una propuesta. */
  detachTurn: () => void;
  reset: () => void;
  // ── Scripted helpers (sin API) ───────────────────────────────────
  // Usados para flujos pre-armados que simulan una conversación sin
  // tocar al agente (ej: el botón "Sorpréndeme"). Cada helper appendea
  // un mensaje al chat con la apariencia de uno real.
  /** Pushea una burbuja de user. */
  pushUserMessage: (text: string) => void;
  /** Pushea una burbuja NUEVA de assistant con el texto dado. */
  pushAssistantText: (text: string) => void;
  /**
   * Appendea una tool card a la última burbuja assistant (la crea si no
   * existe) y devuelve una Promise que se resuelve cuando el user
   * responde. Mismo patrón que `executeTool` interno pero accesible
   * desde fuera.
   */
  pushToolCard: (name: V2ToolCardKind, input: any) => Promise<any>;
}

export const useV2Chat = (
  shortName: string,
  /** Productos REALES del usuario — alimentan el catálogo del system prompt
   *  para que el agente preseleccione IDs que existen de verdad. Antes se
   *  usaba MOCK_USER_PRODUCTS y el agente devolvía IDs falsos (p-4) que la
   *  card real no podía matchear. */
  products: MockProductV2[] = [],
  /** Lo que el Chat le suma (ver `OpcionesV2Chat`). El asistente no pasa nada. */
  opciones: OpcionesV2Chat = {},
): UseV2ChatReturn => {
  // Por ref: se leen dentro de los callbacks sin entrar en sus deps (el objeto cambia de
  // identidad por render). Las claves de storage se fijan al montar.
  const opcionesRef = useRef<OpcionesV2Chat>(opciones);
  opcionesRef.current = opciones;
  const claves = useRef(clavesDeStorage(opciones.prefijoStorage)).current;
  // El chat se persiste en sessionStorage para que sobreviva a la
  // navegación (ej: tocar una imagen generada → ver el detalle → volver
  // atrás). Vive solo en la pestaña actual; al cerrarla se limpia.
  const [messages, setMessages] = useState<V2ChatMessage[]>(() =>
    loadPersistedMessages(claves),
  );
  const [streaming, setStreaming] = useState(false);
  // Espejo síncrono de `streaming`. El guard de `sendMessage` lo lee de acá y
  // no del state porque `detachTurn()` + enviar ocurren en el MISMO tick: con
  // el state (asíncrono) el envío se descartaba en silencio.
  const streamingRef = useRef(false);
  const setStreamingBoth = useCallback((v: boolean) => {
    streamingRef.current = v;
    setStreaming(v);
  }, []);

  // ── Actividad de herramientas server-side (web search / Apify MCP) ──
  // Mientras el agente ejecuta una tool del lado del servidor, mostramos un
  // indicador. Usamos un ref espejo para no llamar setState en cada evento
  // del stream (solo en transiciones reales).
  const [toolActivity, setToolActivity] = useState<string | null>(null);
  const toolActivityRef = useRef<string | null>(null);
  // Si hay una tool SERVER-SIDE corriendo (web search / scraper): son las que pueden
  // callar minutos, y el watchdog del stream les da más aire. Antes se usaba
  // `toolActivityRef` para esto, pero desde que la etiqueta también cubre "Pensando…" y
  // las tools del cliente, ya no distingue (ADR-2026-09-09-el-chat-dice-que-esta-haciendo).
  const servidorEnCursoRef = useRef(false);
  const updateToolActivity = useCallback((v: string | null) => {
    if (toolActivityRef.current === v) return;
    toolActivityRef.current = v;
    setToolActivity(v);
  }, []);
  /** La etiqueta de una tool del cliente: las de pantalla la traen consigo (el editor de
   *  video registra "Cortando el clip…"); las de siempre, la de `actividadDelAgente`. */
  const etiquetaDeCliente = useCallback(
    (nombre: string): string =>
      opcionesRef.current.etiquetaDeHerramienta?.(nombre) ?? etiquetaDeTool(nombre),
    [],
  );

  // ── Adjuntos del menú "+" (chips pre-envío) ─────────────────────────
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);
  // Ref espejo para leer los adjuntos dentro de sendMessage sin meterlos en
  // las deps del useCallback (evita recrearlo en cada chip).
  const attachmentsRef = useRef<ChatAttachment[]>([]);
  attachmentsRef.current = attachments;
  // Las imágenes de referencia del último envío, CON su etiqueta (foto adjunta, pose,
  // fondo). Las lee la card de generación libre al generar; la etiqueta es lo que le
  // permite recortar por prioridad (ADR-2026-09-08-diez-referencias-y-recorte-por-prioridad).
  const referenceImagesRef = useRef<ReferenciaEtiquetada[]>([]);

  const addAttachment = useCallback((entrante: ChatAttachment) => {
    // Un chip de IMAGEN que apunta a un video (cualquier camino que se olvide de
    // distinguirlo) se engancha como video: mandado como foto, Anthropic rechaza el
    // mensaje entero (ADR-2026-09-25-un-video-de-creaciones-se-adjunta-como-video).
    const a = adjuntoCorregidoSiEsVideo(entrante);
    setAttachments((prev) => {
      // Se suman todos, incluidos los MODELOS: hasta acá el modelo era único (el segundo
      // pisaba al primero) y "usá estos dos modelos" llegaba con uno solo
      // (ADR-2026-09-13-varios-modelos-en-la-caja). El Chat fabrica cada modelo con
      // `model-<id>`; los modos viejos (asistente, prompt libre, creador de video) siguen
      // usando el id fijo `model-attachment`, que conserva su lugar único: ahí el
      // selector es de UN modelo y un segundo chip no tendría dónde ir.
      if (a.id === 'model-attachment') {
        return [...prev.filter((x) => x.id !== 'model-attachment'), a];
      }
      if (prev.some((x) => x.id === a.id)) return prev;
      return [...prev, a];
    });
  }, []);
  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => {
      // Un PDF o una planilla que se descuelga ANTES de enviarse no la va a referenciar
      // ningún mensaje: sus bytes se sueltan acá o no se sueltan nunca
      // (ADR-2026-09-09-los-documentos-en-memoria-se-liberan). Mismo criterio que el lote
      // de fotos en `ChatAtlasContext`.
      const fuera = prev.find((x) => x.id === id);
      if (fuera?.kind === 'pdf') liberarPdf(fuera.pdfId);
      else if (fuera?.kind === 'planilla') liberarPlanilla(fuera.planillaId);
      return prev.filter((x) => x.id !== id);
    });
  }, []);
  const clearAttachments = useCallback(() => setAttachments([]), []);
  /**
   * Reemplaza los chips escribiendo TAMBIÉN el ref espejo. Lo necesita la cola de
   * mensajes (ADR-2026-09-13-la-cola-lleva-sus-adjuntos): restaura los adjuntos del
   * mensaje encolado y manda en el MISMO tick, y `sendMessage` lee el ref, no el state,
   * que todavía no se aplicó.
   *
   * No libera nada, a diferencia de `removeAttachment`: los documentos en memoria de un
   * mensaje que está esperando en la cola tienen que seguir vivos hasta que salga.
   */
  const reemplazarAdjuntos = useCallback((lista: ChatAttachment[]) => {
    attachmentsRef.current = lista;
    setAttachments(lista);
  }, []);
  const getReferenceImages = useCallback(
    // Una referencia de producto puede no traer URL (viaja por id, ver
    // `referenciaDeProducto`): acá se piden URLs, así que esa no entra.
    () => referenceImagesRef.current.flatMap((r) => (r.url ? [r.url] : [])),
    [],
  );
  const getReferenciasEtiquetadas = useCallback(() => referenceImagesRef.current, []);
  /** Las preferencias de generación del usuario, para que las cards preseleccionen lo
   *  mismo que el prompt le dice al modelo. Sin opción, ninguna. */
  const recordarPreferencia = useCallback(
    <K extends keyof PreferenciasDeGeneracion>(clave: K, valor: PreferenciasDeGeneracion[K]) => {
      opcionesRef.current.recordarPreferencia?.(clave, valor);
    },
    [],
  );
  const preferenciasDeGeneracion = useCallback(
    (): PreferenciasDeGeneracion => opcionesRef.current.preferencias?.() ?? PREFERENCIAS_POR_DEFECTO,
    [],
  );
  /** Apaga la confirmación de este tipo de acción, si el que monta el hook sabe cómo.
   *  `undefined` = no hay permisos acá y la card no dibuja el botón. */
  const noPreguntarMas = useMemo(
    () =>
      opcionesRef.current.noPreguntarMas
        ? (tipo: TipoAccion) => opcionesRef.current.noPreguntarMas?.(tipo)
        : undefined,
    [],
  );
  // Camino DIRECTO del modo "Crear desde cero": ahí no corre `sendMessage`,
  // así que el modo firma las imágenes adjuntas y las deja acá para que la
  // tool card las lea con `getReferenceImages()`. Todas son fotos que el usuario
  // enganchó, así que entran con esa etiqueta.
  const setReferenceImages = useCallback((urls: string[]) => {
    referenceImagesRef.current = urls.map((url) => ({ url, tipo: 'adjunto' as const }));
  }, []);

  // Pista del toggle Imagen/Video. Solo se manda cuando "Ayuda de IA" está
  // prendida; `null` = el agente decide solo (comportamiento histórico).
  const outputKindHintRef = useRef<'imagen' | 'video' | null>(null);
  const setOutputKindHint = useCallback(
    (kind: 'imagen' | 'video' | null) => {
      outputKindHintRef.current = kind;
    },
    [],
  );

  // Modelo del próximo envío (lo setea el selector de "inteligencia" del
  // Asistente). Default el de nivel Media. El backend valida.
  const modelRef = useRef<string>(MODEL_MEDIA);
  const setModel = useCallback((m: string) => {
    if (m) modelRef.current = m;
  }, []);

  // Investigación externa: la prende el agente vía la tool `activar_investigacion`.
  // Mientras esté apagada, no mandamos web_search/MCP (contexto liviano y barato).
  const researchEnabledRef = useRef<boolean>(loadResearchEnabled(claves));
  const setResearchEnabled = useCallback((on: boolean) => {
    researchEnabledRef.current = on;
    try {
      if (on) sessionStorage.setItem(claves.research, '1');
      else sessionStorage.removeItem(claves.research);
    } catch {
      /* noop */
    }
  }, []);

  // ── Persistencia en `agent_chats` (Supabase) ────────────────────────
  const userIdRef = useRef<string | null>(null);
  const currentChatIdRef = useRef<string | null>(loadCurrentChatId(claves));
  /** Versión (`updated_at` crudo) del chat tal como lo leí o guardé por última vez, y
   *  cuántos mensajes tenía entonces. El guardado va condicionado a esa versión y, si
   *  alguien guardó en el medio, se funde lo remoto con lo mío nuevo
   *  (ADR-2026-10-13-chats-compartidos-con-la-empresa). Se restauran de la pestaña junto
   *  con el chat: si no, el primer guardado después de recargar iba sin condición
   *  (ADR-2026-10-13-review-176-el-chat-compartido-no-pisa-al-companero). */
  const sincroniaInicialRef = useRef(
    (() => {
      try {
        return leerSincronia(sessionStorage.getItem(claves.sincronia), currentChatIdRef.current);
      } catch {
        return null;
      }
    })(),
  );
  const versionDeChatRef = useRef<string | null>(sincroniaInicialRef.current?.version ?? null);
  const largoSincronizadoRef = useRef(sincroniaInicialRef.current?.largo ?? 0);
  /** La respuesta que quedó guardada a medio escribir (ver `fusionarMensajes`). */
  const activaGuardadaRef = useRef<number | null>(sincroniaInicialRef.current?.activa ?? null);
  const fijarSincronia = useCallback(
    (chatId: string | null, version: string | null, largo: number, activa: number | null) => {
      versionDeChatRef.current = version;
      largoSincronizadoRef.current = largo;
      activaGuardadaRef.current = activa;
      try {
        if (chatId && version) {
          sessionStorage.setItem(claves.sincronia, JSON.stringify({ chatId, version, largo, activa }));
        } else {
          sessionStorage.removeItem(claves.sincronia);
        }
      } catch {
        /* sessionStorage puede fallar en modo privado — non-fatal */
      }
    },
    [claves],
  );
  const sesionDeChatRef = useRef(0);
  const sesionDeChat = useCallback(() => sesionDeChatRef.current, []);
  // Para el registro de skills (ADR-2026-09-18-el-uso-de-las-skills-se-registra): el
  // último pedido del usuario, cuántos mandó en este chat y qué skills cargó esta ronda.
  const pedidoRef = useRef('');
  const turnoRef = useRef(0);
  const skillsDeLaRondaRef = useRef<string[]>([]);
  const creatingChatRef = useRef(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Snapshot de los mensajes más recientes — lo lee el save (debounced /
  // al desmontar) sin depender del closure.
  const latestMessagesRef = useRef<V2ChatMessage[]>(messages);
  latestMessagesRef.current = messages;

  // Resumen vigente de la parte vieja del chat, si ya se compactó
  // (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar). Es de ENVÍO: `messages` sigue
  // completo (es lo que el usuario ve, lo que se guarda y donde busca `buscar_en_el_chat`).
  const resumenRef = useRef<EstadoDeResumen | null>(loadPersistedResumen(claves));
  const guardarResumen = useCallback(
    (estado: EstadoDeResumen | null) => {
      resumenRef.current = estado;
      try {
        if (estado) sessionStorage.setItem(claves.resumen, JSON.stringify(estado));
        else sessionStorage.removeItem(claves.resumen);
      } catch {
        /* noop */
      }
    },
    [claves],
  );

  // Guarda el chat en la DB: crea la fila en el primer mensaje (con título =
  // primer mensaje del user truncado) o actualiza `messages` si ya existe.
  const saveChatToDb = useCallback(async () => {
    const companyId = companyIdRef.current;
    const userId = userIdRef.current;
    if (!companyId || !userId || latestMessagesRef.current.length === 0) return;
    try {
      const api = await import('./agentChatApi');
      if (!currentChatIdRef.current) {
        if (creatingChatRef.current) return; // evita doble insert
        creatingChatRef.current = true;
        const sesionAlCrear = sesionDeChatRef.current;
        const msgs = latestMessagesRef.current;
        // Si el agente escribía al tomar la copia, su último mensaje puede estar cortado: se
        // decide con lo que valía JUNTO a la copia, no después del await (review #176).
        const escribiaAlCopiar = streamingRef.current;
        const firstUser = msgs.find((m) => m.role === 'user');
        const creado = await api.crearChatConVersion({
          userId,
          companyId,
          title: firstUser?.text || 'Chat',
          messages: msgs,
          projectId: opcionesRef.current.proyecto?.() ?? null,
        });
        creatingChatRef.current = false;
        // Si en el medio se abrió otro chat o se tocó "Nuevo chat", este id ya no es el de
        // la pantalla: adoptarlo mandaría la conversación nueva a la vieja.
        if (creado && sesionDeChatRef.current === sesionAlCrear) {
          currentChatIdRef.current = creado.id;
          const s = sincroniaTrasGuardar({
            largo: msgs.length,
            escribiendo: escribiaAlCopiar,
            ultimoEsDelAgente: msgs[msgs.length - 1]?.role === 'assistant',
          });
          fijarSincronia(creado.id, creado.version, s.largo, s.activa);
          try {
            sessionStorage.setItem(claves.chatId, creado.id);
          } catch {
            /* noop */
          }
        }
        return;
      }
      // Guardado con versión. Un chat compartido lo pueden estar siguiendo dos personas:
      // si alguien guardó desde que leí, releo, le agrego al final lo mío nuevo y vuelvo
      // a intentar. Tres vueltas alcanzan: el choque es raro y cada vuelta parte de la
      // versión fresca. Un `error` de verdad (red, permisos) corta: el próximo cambio de
      // mensajes vuelve a disparar el guardado.
      //
      // Todo queda atado al chat en el que arrancó (review atlas-code#176): si después de
      // un `await` la pantalla ya es otro chat, se abandona sin tocar nada de la nueva.
      const chatId = currentChatIdRef.current;
      const sesion = sesionDeChatRef.current;
      const sigueSiendoEste = () =>
        sesionDeChatRef.current === sesion && currentChatIdRef.current === chatId;
      for (let intento = 0; intento < 3; intento++) {
        const msgs = latestMessagesRef.current;
        const escribiaAlCopiar = streamingRef.current;
        // Sin versión conocida (un chat restaurado sin su sincronía) no se guarda a ciegas:
        // se relee primero, como en un choque.
        const r = versionDeChatRef.current
          ? await api.guardarMensajesSiNoCambio(chatId, msgs, versionDeChatRef.current)
          : ({ ok: false, motivo: 'conflicto' } as const);
        if (!sigueSiendoEste()) return;
        if (r.ok) {
          const s = sincroniaTrasGuardar({
            largo: msgs.length,
            escribiendo: escribiaAlCopiar,
            ultimoEsDelAgente: msgs[msgs.length - 1]?.role === 'assistant',
          });
          fijarSincronia(chatId, r.version, s.largo, s.activa);
          return;
        }
        if (r.motivo === 'error') return;
        // Con el agente escribiendo no se funde: el texto que llega va al ÚLTIMO mensaje,
        // y la fusión pondría ahí el de un compañero. Se reintenta solo con el próximo
        // cambio, y al terminar el turno se funde con la respuesta completa.
        if (streamingRef.current) return;
        const remoto = await api.cargarChatConVersion(chatId);
        if (!remoto || !sigueSiendoEste() || streamingRef.current) return;
        const locales = latestMessagesRef.current;
        // Sin sincronía previa se asume en común lo que entra en los dos: lo remoto queda
        // entero y sólo se agrega lo local que sobra.
        const largo = versionDeChatRef.current
          ? largoSincronizadoRef.current
          : Math.min(locales.length, remoto.messages.length);
        // Mismo criterio que `loadChatById`: las cards vuelven con su estado guardado.
        const fusion = fusionarMensajes(
          remoto.messages.map((m) => ({ ...m, toolCards: restaurarToolCards(m.toolCards) })),
          locales,
          largo,
          versionDeChatRef.current ? activaGuardadaRef.current : null,
        );
        fijarSincronia(chatId, remoto.version, remoto.messages.length, null);
        latestMessagesRef.current = fusion;
        setMessages(fusion);
      }
    } catch (e) {
      creatingChatRef.current = false;
      console.error('[useV2Chat] saveChatToDb', e);
    }
  }, []);

  // Asegura que exista la fila del chat ANTES de pegarle al agente, para
  // poder mandar `agent_chat_id` y que el edge function impute ahí el costo
  // del agente. La crea con el primer mensaje del user; el save debounced
  // después actualiza el contenido completo.
  const ensureChatCreated = useCallback(async (firstUserText: string) => {
    if (currentChatIdRef.current || creatingChatRef.current) return;
    const companyId = companyIdRef.current;
    const userId = userIdRef.current;
    if (!companyId || !userId) return;
    creatingChatRef.current = true;
    try {
      const { createChat } = await import('./agentChatApi');
      const id = await createChat({
        userId,
        companyId,
        title: firstUserText,
        messages: latestMessagesRef.current.length
          ? latestMessagesRef.current
          : [{ role: 'user', text: firstUserText }],
        projectId: opcionesRef.current.proyecto?.() ?? null,
      });
      if (id) {
        currentChatIdRef.current = id;
        try {
          sessionStorage.setItem(claves.chatId,id);
        } catch {
          /* noop */
        }
      }
    } catch (e) {
      console.error('[useV2Chat] ensureChatCreated', e);
    } finally {
      creatingChatRef.current = false;
    }
  }, []);

  // Suma créditos de imágenes al tally del chat actual.
  const addChatCredits = useCallback((credits: number) => {
    const id = currentChatIdRef.current;
    if (!id || credits <= 0) return;
    import('./agentChatApi')
      .then(({ addChatCredits }) => addChatCredits(id, credits))
      .catch(() => {});
    // Una generación de imagen también descuenta saldo de la empresa →
    // refrescamos el pill de créditos del chrome.
    try {
      window.dispatchEvent(new Event('atlas:credits-updated'));
    } catch {
      /* noop */
    }
  }, []);

  // Lee el total de créditos consumidos en el chat actual.
  const fetchConsumo = useCallback(async (): Promise<number> => {
    const id = currentChatIdRef.current;
    if (!id) return 0;
    try {
      const { fetchChatCredits } = await import('./agentChatApi');
      return await fetchChatCredits(id);
    } catch {
      return 0;
    }
  }, []);

  // Persistir mensajes: sessionStorage instantáneo + DB con debounce.
  useEffect(() => {
    try {
      if (messages.length > 0) {
        sessionStorage.setItem(claves.mensajes,JSON.stringify(messages));
      } else {
        sessionStorage.removeItem(claves.mensajes);
      }
    } catch {
      /* sessionStorage puede fallar en modo privado — non-fatal */
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    if (messages.length > 0) {
      saveTimerRef.current = setTimeout(() => saveChatToDb(), 600);
    }
  }, [messages, saveChatToDb]);

  // Flush al desmontar (cerrar el chat / navegar) para no perder el último
  // turno si quedó dentro del debounce.
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      saveChatToDb();
    };
  }, [saveChatToDb]);

  // Ref con los productos más recientes — `sendMessage` lo lee al armar el
  // prompt, así toma la última carga aunque el fetch resuelva después de
  // montar el hook.
  const productsRef = useRef<MockProductV2[]>(products);
  productsRef.current = products;

  // Resolvers por card. El agente puede mandar varias tools en un solo
  // turno; cada una tiene su propia Promise pendiente. Cuando el user
  // resuelve una, el await correspondiente continúa.
  const cardResolversRef = useRef<Map<string, (value: any) => void>>(new Map());
  // AbortController del stream en curso — lo usan el watchdog de inactividad
  // (conexión muerta) y el botón Detener (stopTurn).
  const turnAbortRef = useRef<AbortController | null>(null);
  const companyIdRef = useRef<string | null>(null);
  // Skills de la empresa: se leen fresco en cada envío (con las memorias) y se refrescan
  // acá mismo cuando el agente guarda una, así `cargar_skill` la encuentra en el mismo
  // turno (ADR-2026-09-09-el-agente-tiene-skills).
  const skillsEmpresaRef = useRef<SkillDelAgente[]>([]);
  const projectIdRef = useRef<string>(getEphemeralProjectId());
  // Token de la "generación" actual. Bump on reset() para que cualquier
  // loop pendiente sepa que tiene que cortar (evitamos respuestas viejas
  // que aparecen en un chat nuevo).
  const generationRef = useRef<number>(0);

  // Cargar id_company + user_id del usuario al montar (para persistir chats).
  useEffect(() => {
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;
        userIdRef.current = user.id;
        const { data: profile } = await supabase
          .from('profiles')
          .select('id_company')
          .eq('id', user.id)
          .single();
        if (profile && (profile as any).id_company) {
          companyIdRef.current = (profile as any).id_company as string;
        }
      } catch {
        // silent
      }
    })();
  }, []);

  // ── Active card derivada ────────────────────────────────────────
  // La card activa es la PRIMERA pending en orden temporal. Si el agente
  // mandó 3 tools en un turno, se muestran en orden de aparición; al
  // resolver una, la siguiente pasa a ser activa automáticamente.
  const activeToolCard = useMemo<V2ToolCard | null>(() => {
    for (const m of messages) {
      if (!m.toolCards) continue;
      for (const c of m.toolCards) {
        if (c.status === 'pending' && !c.detached) return c;
      }
    }
    return null;
  }, [messages]);

  // Batch actual = toolCards del último mensaje del agente que tenga
  // cards. Sirve para mostrar "Paso X de Y" en la UI.
  const { batchSize, activeIndex } = useMemo(() => {
    let batch: V2ToolCard[] = [];
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.role === 'assistant' && m.toolCards && m.toolCards.length > 0) {
        batch = m.toolCards;
        break;
      }
    }
    if (!activeToolCard) {
      return { batchSize: batch.length, activeIndex: 0 };
    }
    const idx = batch.findIndex((c) => c.id === activeToolCard.id);
    return { batchSize: batch.length, activeIndex: idx >= 0 ? idx + 1 : 0 };
  }, [messages, activeToolCard]);

  // ── Append helpers ───────────────────────────────────────────────
  // Estos siempre operan sobre la ÚLTIMA burbuja assistant (que se crea al
  // inicio de cada ronda en sendMessage). Si por alguna razón no hay
  // burbuja assistant pendiente, hacemos no-op — preferimos perder un
  // delta vs duplicar bubbles.
  const appendAssistantText = useCallback((delta: string) => {
    if (!delta) return;
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant') return prev;
      const next = [...prev];
      next[next.length - 1] = { ...last, text: last.text + delta };
      return next;
    });
  }, []);

  /** Le saca al último mensaje del asistente los `[registro:]` que haya escrito él. */
  const sanearTextoDelAssistant = useCallback(() => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant' || !last.text.includes('[registro:')) return prev;
      const next = [...prev];
      next[next.length - 1] = { ...last, text: sinRegistrosDelModelo(last.text) };
      return next;
    });
  }, []);

  const appendToolCard = useCallback((card: V2ToolCard) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      if (last.role !== 'assistant') return prev;
      const next = [...prev];
      next[next.length - 1] = {
        ...last,
        toolCards: [...(last.toolCards || []), card],
      };
      return next;
    });
  }, []);

  // Cuelga gráficos / sugerencias del ÚLTIMO mensaje del asistente — para las
  // herramientas silenciosas del que monta el hook (las del Chat). Los gráficos se
  // ACUMULAN (un turno puede traer más de uno); las sugerencias se reemplazan.
  const adjuntarAlUltimoAssistant = useCallback(
    (patch: Partial<V2ChatMessage>) => {
      setMessages((prev) => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        if (last.role !== 'assistant') return prev;
        const next = [...prev];
        next[next.length - 1] = {
          ...last,
          graficos: patch.graficos
            ? [...(last.graficos ?? []), ...patch.graficos]
            : last.graficos,
          sugerencias: patch.sugerencias ?? last.sugerencias,
          desbloquear: patch.desbloquear ?? last.desbloquear,
          verCalendario: patch.verCalendario ?? last.verCalendario,
          registros: patch.registros ? [...(last.registros ?? []), ...patch.registros] : last.registros,
          acciones: patch.acciones ? [...(last.acciones ?? []), ...patch.acciones] : last.acciones,
          imagenesWeb: patch.imagenesWeb ? [...(last.imagenesWeb ?? []), ...patch.imagenesWeb] : last.imagenesWeb,
        };
        return next;
      });
    },
    [],
  );

  /**
   * La línea de una acción se muestra cuando la acción TERMINÓ: no cuando la herramienta devuelve
   * (ahí el agente todavía está leyendo lo que trajo, con la misma etiqueta), sino cuando pasa a otra
   * cosa —escribe, arranca otra herramienta o cierra el turno—. Mientras tanto queda pendiente, con el
   * mensaje de SU ronda: para entonces ya hay otro abajo
   * (ADR-2026-10-22-cada-accion-del-asistente-queda-en-una-linea-2).
   */
  const indiceDeLaRondaRef = useRef(-1);
  const accionPendienteRef = useRef<{ indice: number; nombre: string } | null>(null);
  const soltarAccion = useCallback(() => {
    const pendiente = accionPendienteRef.current;
    if (!pendiente) return;
    accionPendienteRef.current = null;
    setMessages((prev) => {
      const m = prev[pendiente.indice];
      if (!m || m.role !== 'assistant') return prev;
      const next = [...prev];
      next[pendiente.indice] = { ...m, acciones: [...(m.acciones ?? []), pendiente.nombre] };
      return next;
    });
  }, []);

  const updateToolCard = useCallback(
    (cardId: string, patch: Partial<V2ToolCard>) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (!m.toolCards) return m;
          if (!m.toolCards.some((c) => c.id === cardId)) return m;
          return {
            ...m,
            toolCards: m.toolCards.map((c) =>
              c.id === cardId ? { ...c, ...patch } : c,
            ),
          };
        }),
      );
    },
    [],
  );

  // ── Tool execution ────────────────────────────────────────────────
  // Cuando el agente manda varias tools en un turno, las "scheduleamos"
  // todas a la vez: cada una se appendea como pending y devuelve su propia
  // Promise. La primera queda activa (derivado por useMemo arriba); las
  // demás esperan en la queue. El user las va resolviendo en orden y cada
  // await continúa de forma independiente.
  const executeTool = useCallback(
    (toolUseId: string, name: string, input: any): Promise<any> => {
      // Herramientas SILENCIOSAS del que monta el hook (las del Chat: respuesta
      // gráfica, sugerencias): se resuelven solas, sin tarjeta, y pueden colgar algo del
      // último mensaje del asistente (ADR-2026-09-08-el-chat-toma-el-motor-del-asistente).
      const silenciosa = opcionesRef.current.herramientasSilenciosas?.[name];
      if (silenciosa) {
        return Promise.resolve(silenciosa(input, { adjuntarAlUltimoAssistant }));
      }
      // Las de PANTALLA (el editor de video): existen sólo mientras esa pantalla está
      // montada, y las ejecuta ella (ADR-2026-09-11-el-director-del-estudio-se-funde-en-
      // el-agente).
      const dePantalla = opcionesRef.current.resolverHerramienta?.(name);
      if (dePantalla) {
        return Promise.resolve(dePantalla(input, { adjuntarAlUltimoAssistant }));
      }

      // `ver_productos`: silenciosa. El `tool_result` lleva BLOQUES (texto + imágenes
      // por URL firmada de la miniatura del catálogo) y el modelo las ve en este mismo
      // turno (ADR-2026-09-08-el-chat-trabaja-en-lote). `__bloques` es la marca para que
      // el loop mande el array como contenido en vez de serializarlo.
      if (name === 'ver_productos') {
        return (async () => {
          const ids = idsPedidos(input);
          // Las fotos de cada producto CON su vista: es lo que le dice al agente de qué
          // lados hay foto (ADR-2026-09-13-el-agente-sabe-que-vistas-tiene-cada-producto).
          // Si la consulta falla o tarda, se muestra la portada sola, como siempre.
          let vistas: Map<string, FotoDeProducto[]> | undefined;
          const cid = companyIdRef.current;
          if (cid && ids.length > 0) {
            try {
              vistas = await withTimeout(fetchProductVistas(cid, ids), 10_000, 'vistas');
            } catch {
              vistas = undefined;
            }
          }
          return { __bloques: bloquesVerProductos(ids, productsRef.current, vistas) };
        })();
      }

      // `buscar_productos`: silenciosa y gratis. La misma búsqueda que los buscadores de la app, sobre
      // el catálogo que ve el agente: por SKU, categoría o características, no sólo por nombre
      // (ADR-2026-10-22-el-agente-busca-productos-por-sku-categoria-y-caracteristicas).
      if (name === 'buscar_productos') {
        return (async () => {
          // Sin consulta, lista (ADR-2026-10-22-el-agente-busca-productos-por-sku-categoria-y-caracteristicas-2).
          const consulta = consultaDe(input);
          const productos = productsRef.current;
          if (productos.length === 0) return { __texto: 'El catálogo está vacío: no hay productos para buscar.' };
          let indice: ReadonlyMap<string, string> = new Map();
          let resumen: (id: string) => ResumenParaBuscar | undefined = () => undefined;
          try {
            const r = await withTimeout(indiceParaBuscar(productos.map((p) => p.id)), 15_000, 'indice');
            indice = r.indice;
            resumen = r.resumen;
          } catch {
            // Sin los datos, se busca sólo por nombre (y el resultado no trae SKU ni categoría).
          }
          return {
            __texto: resultadoDeBusqueda(productos, consulta, indice, resumen, {
              limite: limiteDe(input, consulta !== null),
              desde: desdeDe(input),
              soloConSku: soloConSkuDe(input),
            }),
          };
        })();
      }

      // `buscar_en_el_chat`: silenciosa, local y gratis. Sólo se le ofrece cuando la
      // conversación ya se compactó; busca sobre `messages`, que sigue COMPLETO aunque el
      // historial que viaja esté resumido
      // (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar).
      if (name === 'buscar_en_el_chat') {
        const consulta = typeof input?.consulta === 'string' ? input.consulta : '';
        const limite = typeof input?.limite === 'number' ? input.limite : undefined;
        return Promise.resolve({
          __texto: buscarEnElChat(latestMessagesRef.current, consulta, limite),
        });
      }

      // `abrir_web`: silenciosa. El backend lee la página y guarda sus imágenes como adjuntos
      // del chat; quedan colgadas de ESTE mensaje del agente (`imagenesWeb`), así `adjuntoPorId`
      // las encuentra para las referencias, los moodboards y agregar_productos
      // (ADR-2026-10-15-el-agente-abre-paginas-web).
      if (name === 'abrir_web') {
        return (async () => {
          const entrada = entradaDeAbrirWeb(input);
          if (!entrada) return { __texto: 'Falta el link: pasá `url` (o `urls_de_imagenes`).' };
          // La lectura tarda y no se cancela al cambiar de chat: si el turno ya se descartó
          // (otro chat, Detener), las imágenes no se cuelgan del último mensaje de OTRA conversación.
          const turno = generationRef.current;
          const r = await pedirPagina(entrada);
          if (turno !== generationRef.current) return { __texto: 'La conversación cambió mientras se leía la página.' };
          if ('error' in r) return { __texto: r.error };
          const imagenes = imagenesWebDe(r);
          if (imagenes.length > 0) adjuntarAlUltimoAssistant({ imagenesWeb: imagenes });
          const firmas = new Map<string, string>();
          await Promise.all(
            imagenes.map(async (i) => {
              const url = await signReferenceImage(i.storagePath, i.bucket).catch(() => null);
              if (url) firmas.set(i.id, url);
            }),
          );
          return { __bloques: bloquesDeLaPagina(r, firmas) };
        })();
      }

      // `ver_fotos`: silenciosa, misma mecánica. Las fotos que el usuario adjuntó en
      // montón no viajan en su mensaje; acá se sube al storage el tramo que el modelo
      // pidió y vuelven como bloques de imagen
      // (ADR-2026-09-08-el-agente-mira-las-fotos-de-a-diez).
      if (name === 'ver_fotos') {
        return (async () => {
          // Con `fotos`, mira las SEÑALADAS por id, del tipo que sean: el agente pasa el
          // id y el resolver busca dónde vive esa imagen
          // (ADR-2026-09-22-mirar-una-imagen-no-depende-de-su-tipo).
          const pedidos = idsDeFotosPedidas(input);
          if (pedidos.length > 0) {
            const companyId = companyIdRef.current;
            const loteParaIds = ultimoLoteAdjuntado(latestMessagesRef.current);
            // Los modelos se traen UNA vez para todos los ids, no uno por uno.
            let modelos: ModelV2[] | null = null;
            const listaDeModelos = async (): Promise<ModelV2[]> => {
              if (!modelos) modelos = companyId ? await fetchUserModels(companyId) : [];
              return modelos;
            };
            const { fotos, inexistentes } = await resolverImagenes(pedidos, {
              adjunto: async (id) => {
                const a = adjuntoPorId(latestMessagesRef.current, id);
                return a ? { url: await signReferenceImage(a.storagePath, a.bucket) } : null;
              },
              monton: async (id) => {
                // Se verifica que exista ANTES de subirla: un id inventado no dispara un
                // upload (el montón vive como File y se materializa a demanda).
                const enElLote = loteParaIds
                  ? fotosDelLote(loteParaIds.loteId).find((f) => f.id === id)
                  : undefined;
                if (!loteParaIds || !enElLote) return null;
                return {
                  url: await urlDeFotoDelLote(loteParaIds.loteId, id, companyId),
                  nombre: enElLote.archivo.name,
                };
              },
              producto: async (id) => {
                const p = productsRef.current.find((x) => String(x.id) === id);
                if (!p) return null;
                const full = companyId ? await getProductFullImageUrl(companyId, id) : null;
                return { url: full || p.imageUrl || null, nombre: p.name };
              },
              modelo: async (id) => {
                const m = (await listaDeModelos()).find((x) => String(x.id) === id);
                return m ? { url: m.thumbnailUrl || null, nombre: m.nombre } : null;
              },
              generada: async (id) => {
                if (!companyId) return null;
                const { preloadGeneratedImages } = await import('../../utils/preloadGeneratedImages');
                const imgs = await preloadGeneratedImages([id], companyId);
                return imgs[0]?.signedUrl ? { url: imgs[0].signedUrl } : null;
              },
              // Pose y fondo se eligen de una LISTA DE NOMBRES: al mirarlos, el nombre es
              // lo que le dice al agente cuál de todos está viendo.
              pose: async (id) => poseGuardadaPorId(id, companyId ?? ''),
              fondo: async (id) => fondoGuardadoPorId(id, companyId ?? ''),
            });
            return { __bloques: bloquesVerImagenes({ fotos, inexistentes }) };
          }
          const lote = ultimoLoteAdjuntado(latestMessagesRef.current);
          const total = lote?.cantidad ?? 0;
          const ventana = ventanaDeFotos(total, input);
          if (!lote || ventana.indices.length === 0) {
            return { __bloques: bloquesVerFotos({ total, desde: ventana.desde, fotos: [] }) };
          }
          // El lote vive en memoria de la pestaña: si hubo recarga, la cantidad sigue en
          // el mensaje pero los archivos no están.
          const disponibles = fotosDelLote(lote.loteId);
          if (disponibles.length === 0) return { __bloques: bloquesFotosPerdidas() };
          const elegidas = ventana.indices
            .map((i) => disponibles[i])
            .filter((f): f is (typeof disponibles)[number] => Boolean(f));
          const urls = await urlsDeFotosDelLote(
            lote.loteId,
            elegidas.map((f) => f.id),
            companyIdRef.current,
          );
          return {
            __bloques: bloquesVerFotos({
              total,
              desde: ventana.desde,
              fotos: elegidas.map((f, i) => ({
                id: f.id,
                nombre: f.archivo.name,
                url: urls[i] ?? null,
              })),
            }),
          };
        })();
      }

      // `ver_planilla`: silenciosa. Lee del registro en memoria el tramo de filas que el
      // agente pidió (ADR-2026-09-08-el-agente-lee-y-escribe-planillas).
      if (name === 'ver_planilla') {
        const pedido = (input as { archivo?: unknown } | null)?.archivo;
        const { elegido, nombres, noEncontrado } = elegirPlanillaAdjuntada(
          latestMessagesRef.current,
          typeof pedido === 'string' ? pedido : null,
        );
        if (noEncontrado) {
          return Promise.resolve({
            texto: `No hay ninguna planilla que se llame "${String(pedido)}". Las adjuntas son: ${nombres
              .map((n) => `"${n}"`)
              .join(', ')}. Volvé a llamar con una de esas en \`archivo\`.`,
          });
        }
        const planilla = obtenerPlanilla(elegido?.planillaId);
        if (elegido && !planilla) {
          return Promise.resolve({
            texto:
              'La planilla ya no está disponible (se recargó la página desde que la adjuntó). Pedile al usuario que la vuelva a adjuntar con el "+".',
          });
        }
        return Promise.resolve({
          texto: lecturaDePlanilla(planilla, ventanaDeFilas(planilla, input)),
        });
      }

      // `ver_pdf`: silenciosa. Con capa de texto devuelve el texto de esas páginas; sin
      // ella las RENDERIZA y las manda como bloques de imagen, que es la única forma de
      // entender un escaneado (ADR-2026-09-08-el-agente-lee-y-crea-pdfs).
      if (name === 'ver_pdf') {
        return (async () => {
          const pedido = (input as { archivo?: unknown } | null)?.archivo;
          const { elegido, nombres, noEncontrado } = elegirPdfAdjuntado(
            latestMessagesRef.current,
            typeof pedido === 'string' ? pedido : null,
          );
          if (noEncontrado) {
            return {
              texto: `No hay ningún PDF que se llame "${String(pedido)}". Los adjuntos son: ${nombres
                .map((n) => `"${n}"`)
                .join(', ')}. Volvé a llamar con uno de esos en \`archivo\`.`,
            };
          }
          const pdf = obtenerPdf(elegido?.pdfId);
          if (elegido && !pdf) {
            return {
              texto:
                'El PDF ya no está disponible (se recargó la página desde que lo adjuntó). Pedile al usuario que lo vuelva a adjuntar.',
            };
          }
          const v = ventanaDePaginas(pdf, input);
          if (!pdf || !v.comoImagen) return { texto: lecturaDePdf(pdf, v) };
          try {
            const { renderizarPaginas } = await import('../../utils/pdf/leerPdf');
            const imgs = await renderizarPaginas(pdf.bytes, v.desde, v.cantidad);
            if (imgs.length === 0) {
              return { texto: 'No pude renderizar esas páginas. Probá con otro tramo.' };
            }
            return {
              __bloques: [
                { type: 'text', text: encabezadoDeImagenes(pdf, v, imgs.length) },
                ...imgs.map((url) => ({ type: 'image', source: { type: 'url', url } })),
              ],
            };
          } catch (err) {
            console.warn('[ver_pdf] render', err);
            return { texto: 'No pude abrir el PDF para renderizarlo.' };
          }
        })();
      }

      // `extraer_fotos_del_pdf`: silenciosa. Saca las FOTOS embebidas de un PDF adjunto (una ficha
      // técnica, un informe de fábrica), las sube como adjuntos del chat (`pdf-…`, igual que las
      // `web-…` de abrir_web) y se las muestra al agente en hojas de contacto numeradas, que
      // pesan mucho menos que cada foto suelta (ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf).
      if (name === 'extraer_fotos_del_pdf') {
        return (async () => {
          const pedido = (input as { archivo?: unknown } | null)?.archivo;
          const { elegido, nombres, noEncontrado } = elegirPdfAdjuntado(
            latestMessagesRef.current,
            typeof pedido === 'string' ? pedido : null,
          );
          if (noEncontrado) {
            return {
              texto: `No hay ningún PDF que se llame "${String(pedido)}". Los adjuntos son: ${nombres
                .map((n) => `"${n}"`)
                .join(', ')}. Volvé a llamar con uno de esos en \`archivo\`.`,
            };
          }
          const pdf = obtenerPdf(elegido?.pdfId);
          if (!pdf) {
            return {
              texto: elegido
                ? 'El PDF ya no está disponible (se recargó la página desde que lo adjuntó). Pedile al usuario que lo vuelva a adjuntar.'
                : 'No hay ningún PDF adjunto en la conversación. Pedile al usuario que lo adjunte con el "+".',
            };
          }
          const companyId = companyIdRef.current;
          if (!companyId) return { texto: 'No pude identificar la empresa para guardar las fotos.' };
          const turno = generationRef.current;
          try {
            const { extraerFotosDelPdf, hojasDeContacto } = await import('../../utils/pdf/leerPdf');
            const { fotos, descartadas } = await extraerFotosDelPdf(pdf.bytes, { paginas: paginasPedidas(input) });
            // Subidas de a 6: un informe de fábrica trae decenas y una que falla no corta el resto.
            const subidas: Array<{ foto: (typeof fotos)[number]; id: string; storagePath: string; bucket: string }> = [];
            for (let i = 0; i < fotos.length; i += 6) {
              const tramo = await Promise.all(
                fotos.slice(i, i + 6).map((foto) =>
                  subirFotoDelPdf(foto.blob, companyId)
                    .then((r) => ({ foto, ...r }))
                    .catch((err) => {
                      console.warn('[extraer_fotos_del_pdf] subida', err);
                      return null;
                    }),
                ),
              );
              for (const t of tramo) if (t) subidas.push(t);
            }
            if (turno !== generationRef.current) return { __texto: 'La conversación cambió mientras sacaba las fotos.' };
            const numeradas: FotoDelPdfSubida[] = subidas.map((s, k) => ({ numero: k + 1, id: s.id, pagina: s.foto.pagina }));
            if (subidas.length > 0) {
              adjuntarAlUltimoAssistant({
                imagenesWeb: subidas.map((s) => ({
                  id: s.id,
                  storagePath: s.storagePath,
                  bucket: s.bucket,
                  urlOriginal: `${pdf.nombre} · página ${s.foto.pagina}`,
                })),
              });
            }
            const hojas = await hojasDeContacto(
              subidas.map((s) => s.foto),
              numeradas.map((f) => f.numero),
            );
            return {
              __bloques: [
                { type: 'text', text: textoDeFotosDelPdf(pdf.nombre, numeradas, descartadas + fotos.length - subidas.length, hojas.length) },
                ...hojas.map((url) => ({ type: 'image', source: { type: 'url', url } })),
              ],
            };
          } catch (err) {
            console.warn('[extraer_fotos_del_pdf]', err);
            return { texto: 'No pude abrir el PDF para sacarle las fotos. Probá con ver_pdf (como: "imagen").' };
          }
        })();
      }

      // `guardar_datos_del_producto`: silenciosa. Lo que el agente leyó (una ficha técnica, un
      // informe de fábrica, lo que le dictaron) va a la ficha del producto: medidas, características
      // y paquete de envío (ADR-2026-10-18-el-agente-saca-las-fotos-de-un-pdf).
      if (name === 'guardar_datos_del_producto') {
        return (async () => {
          const [{ guardarDatosDelProducto }, api] = await Promise.all([
            import('./guardarDatosDelProducto'),
            import('./medidasApi'),
          ]);
          const texto = await guardarDatosDelProducto(input, {
            nombreDelProducto: (id) => productsRef.current.find((p) => p.id === id)?.name ?? null,
            leerMedidas: async (id) => (await api.fetchMedidasPorVariante([id]))[id] ?? null,
            guardarMedidas: api.saveMedidasDeVariante,
            leerFicha: async (id) => (await api.fetchFichaPorVariante([id]))[id] ?? { campos: [] },
            guardarFicha: api.saveFichaDeVariante,
            // Lo escribió el agente: revisable, no confirmado (ADR-2026-10-19-revision-greptile-pdf-y-catalogo).
            guardarPaquete: (id, p, o) => api.savePaqueteDeVariante(id, p, { ...o, origen: 'ia' }),
          });
          return { texto };
        })();
      }

      // `ver_video`: silenciosa. El video está en el storage (no en memoria): el backend lo
      // baja y se lo manda a Gemini con la pregunta del agente; vuelve el desglose en texto
      // (ADR-2026-09-11-el-agente-entiende-videos-con-gemini).
      if (name === 'ver_video') {
        return (async () => {
          const pedido = input as { archivo?: unknown; pregunta?: unknown } | null;
          const { elegido, nombres, noEncontrado } = elegirVideoAdjuntado(
            latestMessagesRef.current,
            typeof pedido?.archivo === 'string' ? pedido.archivo : null,
          );
          if (noEncontrado) {
            return {
              texto: `No hay ningún video que se llame "${String(pedido?.archivo)}". Los adjuntos son: ${nombres
                .map((n) => `"${n}"`)
                .join(', ')}. Volvé a llamar con uno de esos en \`archivo\`.`,
            };
          }
          if (!elegido) {
            return {
              texto:
                'El usuario no adjuntó ningún video en esta conversación. Pedile que lo adjunte con el "+" (MP4 o MOV, hasta 100 MB y 5 minutos).',
            };
          }
          const { analizarVideo } = await import('./verVideoApi');
          const r = await analizarVideo({
            storagePath: elegido.storagePath,
            bucket: elegido.bucket ?? 'video-project-assets',
            pregunta: typeof pedido?.pregunta === 'string' ? pedido.pregunta : '',
            agentChatId: currentChatIdRef.current,
          });
          if (!r.ok) return { texto: r.motivo };
          const cabecera = [
            `[Análisis del video "${elegido.name}"`,
            elegido.duracionSeg ? `${formatearDuracion(elegido.duracionSeg)}` : '',
            r.costoCreditos ? `${Math.round(r.costoCreditos * 100) / 100} crédito(s)` : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return { texto: `${cabecera}]\n${r.texto}` };
        })();
      }

      // `extraer_frames`: silenciosa. El backend saca los cuadros con ffmpeg y los deja como
      // imágenes con image_id (ocultas en Creaciones); acá se le muestran al modelo con su
      // tiempo e id, listos para ser frame o referencia con source "generada"
      // (ADR-2026-09-11-el-agente-copia-un-video-con-sus-frames).
      if (name === 'extraer_frames') {
        return (async () => {
          const pedido = input as { archivo?: unknown; tiempos?: unknown } | null;
          const { elegido, nombres, noEncontrado } = elegirVideoAdjuntado(
            latestMessagesRef.current,
            typeof pedido?.archivo === 'string' ? pedido.archivo : null,
          );
          if (noEncontrado) {
            return {
              texto: `No hay ningún video que se llame "${String(pedido?.archivo)}". Los adjuntos son: ${nombres
                .map((n) => `"${n}"`)
                .join(', ')}.`,
            };
          }
          if (!elegido) {
            return { texto: 'El usuario no adjuntó ningún video en esta conversación. Pedile que lo adjunte con el "+".' };
          }
          const tiempos = (Array.isArray(pedido?.tiempos) ? pedido.tiempos : [])
            .map((t) => (typeof t === 'number' ? t : Number(t)))
            .filter((t) => Number.isFinite(t) && t >= 0)
            .slice(0, 6);
          if (tiempos.length === 0) {
            return { texto: 'Faltan los `tiempos` (segundos) de los cuadros a sacar, por ejemplo [0.5, 3, 7].' };
          }
          const { extraerFramesDeVideo } = await import('./verVideoApi');
          const r = await extraerFramesDeVideo({
            storagePath: elegido.storagePath,
            bucket: elegido.bucket ?? 'video-project-assets',
            tiempos,
          });
          if (!r.ok) return { texto: r.motivo };
          const companyId = companyIdRef.current;
          const { preloadGeneratedImages } = await import('../../utils/preloadGeneratedImages');
          const imgs = companyId
            ? await preloadGeneratedImages(r.frames.map((f) => f.imageId), companyId)
            : [];
          const urlDe = new Map(imgs.map((i) => [String(i.id), i.signedUrl]));
          const bloques: Array<Record<string, unknown>> = [
            {
              type: 'text',
              text: `Frames del video "${elegido.name}"${
                r.duracionSeg ? ` (${formatearDuracion(r.duracionSeg)})` : ''
              }, en orden. Cada uno ya es una imagen del chat: usá su image_id con source "generada" como first_frame / last_frame de generate_video o como referencia de generate_image. Miralos de verdad antes de decidir cuál usar tal cual y cuál editar.`,
            },
          ];
          r.frames.forEach((f, i) => {
            const url = urlDe.get(f.imageId);
            bloques.push({
              type: 'text',
              text: `#${i + 1} · ${formatearDuracion(f.tiempoSeg) || '0 s'} · image_id=${f.imageId}${f.formato ? ` · ${f.formato}` : ''}${url ? ':' : ' (no se pudo cargar la miniatura)'}`,
            });
            if (url) bloques.push({ type: 'image', source: { type: 'url', url } });
          });
          bloques.push({
            type: 'text',
            text: `[registro: frames extraídos — ${r.frames
              .map((f, i) => `#${i + 1} ${formatearDuracion(f.tiempoSeg) || '0 s'} image_id=${f.imageId}`)
              .join(' · ')}]`,
          });
          return { __bloques: bloques };
        })();
      }

      // `ver_lanzamiento`: silenciosa. Lee el lanzamiento y devuelve el texto con el `id`
      // de cada fila, que es lo que el modelo después manda en `product_ids`
      // (ADR-2026-09-09-el-agente-edita-lanzamientos).
      if (name === 'ver_lanzamiento') {
        return (async () => {
          const companyId = companyIdRef.current;
          if (!companyId) return { texto: 'Todavía estoy cargando la cuenta. Probá de nuevo.' };
          const pedido = (input as { lanzamiento_id?: string } | null)?.lanzamiento_id;
          const lanz = await lanzamientoParaElAgente(companyId, pedido);
          if (!lanz) {
            return {
              texto: pedido
                ? 'No encontré ese lanzamiento.'
                : 'No hay ningún lanzamiento en borrador. El usuario puede crear uno desde Lanzamientos.',
            };
          }
          const detalle = await fetchLanzamiento(lanz.id, companyId);
          if (!detalle) return { texto: 'No pude leer ese lanzamiento.' };
          return {
            lanzamiento_id: lanz.id,
            texto: resumenDeLanzamiento({
              nombre: detalle.nombre,
              estado: detalle.estado,
              productos: detalle.productos_,
              // Todas sus tiendas, no sólo la primera (ADR-2026-09-30-lanzamiento-a-varias-tiendas).
              canal: nombresDeDestinos(destinosDe(detalle)),
            }),
          };
        })();
      }

      // `cargar_skill`: silenciosa. Devuelve el instructivo completo de una skill, de
      // fábrica o de la empresa (ADR-2026-09-09-el-agente-tiene-skills).
      if (name === 'cargar_skill') {
        const todas = unirSkills(SKILLS_DE_FABRICA, skillsEmpresaRef.current);
        const r = resultadoDeCargarSkill(input, todas);
        // Registro de uso, para decidir con datos cómo partir las skills pesadas
        // (ADR-2026-09-18-el-uso-de-las-skills-se-registra). Nunca frena la carga.
        try {
          const origen = r.cargada ? (todas.find((sk) => sk.nombre === r.cargada)?.origen ?? null) : null;
          trackEvent(
            EVENTO_SKILL_CARGADA,
            propsDeSkillCargada({
              pedida: input?.nombre,
              cargada: r.cargada,
              origen,
              chatId: currentChatIdRef.current,
              companyId: companyIdRef.current,
              turno: turnoRef.current,
              yaCargadas: skillsDeLaRondaRef.current,
              pedido: pedidoRef.current,
            }),
          );
          if (r.cargada) skillsDeLaRondaRef.current = [...skillsDeLaRondaRef.current, r.cargada];
        } catch {
          /* el registro nunca rompe la carga */
        }
        return Promise.resolve(r);
      }

      // `guardar_skill`: silenciosa, como save_memory. Valida y acota lo que mandó el
      // modelo, hace upsert por nombre y refresca el catálogo en memoria para que el
      // próximo turno (y un `cargar_skill` de esta misma ronda) la vean.
      if (name === 'guardar_skill') {
        return (async () => {
          const companyId = companyIdRef.current;
          const r = normalizarSkillNueva(input, NOMBRES_DE_FABRICA);
          if (!r.ok || !companyId) return resultadoDeGuardarSkill(r, false);
          const guardada = await guardarSkillDeEmpresa(companyId, r.skill);
          if (guardada) {
            skillsEmpresaRef.current = [
              guardada,
              ...skillsEmpresaRef.current.filter((s) => s.nombre !== guardada.nombre),
            ];
          }
          return resultadoDeGuardarSkill(r, Boolean(guardada));
        })();
      }

      // `save_memory` se ejecuta en silencio (no muestra card): escribe en
      // `agent_memories` y resuelve. Las memorias se re-inyectan al prompt
      // en el próximo chat/turno.
      if (name === 'save_memory') {
        return (async () => {
          const companyId = companyIdRef.current;
          if (companyId && input?.content) {
            try {
              await saveMemory(
                companyId,
                String(input.content),
                input.key ? String(input.key) : undefined,
              );
              return { saved: true };
            } catch {
              return { saved: false };
            }
          }
          return { saved: false };
        })();
      }

      // Gateway tool: muestra una card de CONFIRMACIÓN antes de habilitar los
      // scrapers (pueden consumir varios créditos). Si el usuario acepta, prende
      // `researchEnabled` y el edge function adjunta el Apify MCP en los próximos
      // envíos; si cancela, no se habilita nada. La búsqueda web NO pasa por acá
      // (está siempre disponible, server-side).
      if (name === 'activar_investigacion') {
        // Con el permiso en directo no hay card: se prende y se sigue. Mostrar una card
        // "confirmada de fábrica" sería peor que no mostrarla — un botón que ya se apretó
        // solo (ADR-2026-09-08-permisos-tambien-para-el-catalogo).
        if (opcionesRef.current.sinConfirmacion?.('activar_investigacion')) {
          setResearchEnabled(true);
          return Promise.resolve({
            activado: true,
            nota: 'El usuario tiene la investigación en directo: ya quedó habilitada, sin preguntarle.',
          });
        }
        const card: V2ToolCard = {
          id: toolUseId,
          kind: 'confirm_research',
          input,
          status: 'pending',
        };
        appendToolCard(card);
        return new Promise((resolve) => {
          cardResolversRef.current.set(toolUseId, (response: any) => {
            const accepted = !!response?.accepted;
            if (accepted) setResearchEnabled(true);
            updateToolCard(toolUseId, {
              status: accepted ? 'resolved' : 'cancelled',
              response,
            });
            resolve(
              accepted
                ? { activado: true }
                : { activado: false, cancelado: true },
            );
            cardResolversRef.current.delete(toolUseId);
          });
        });
      }

      // Lo que llega hasta acá se contesta con una CARD, así que el nombre tiene que ser
      // de card. Si no lo es, nadie la dibuja: la tarjeta queda invisible y su promesa no
      // se resuelve nunca — el turno se cuelga hasta que el usuario aprieta Detener. Pasa
      // de verdad con las herramientas de PANTALLA (viajaron en el pedido y el usuario se
      // fue del editor antes de la respuesta, así que `resolverHerramienta` ya no las
      // conoce) y con cualquier nombre inventado. Se le contesta al modelo con el motivo
      // (ADR-2026-09-13-una-tool-sin-card-no-cuelga-el-turno).
      if (!esKindDeCard(name)) {
        return Promise.resolve({
          __texto: `La herramienta "${name}" ya no está disponible: la pantalla que la ofrecía se cerró. No la vuelvas a llamar; contale al usuario qué quedó sin hacer.`,
          __error: true,
        });
      }

      // ── Lote ABIERTO (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend) ──
      // Un lote grande se arma de a sets: la primera llamada con `cerrar: false` crea la
      // card en fase "armando" y vuelve AL INSTANTE (el agente sigue escribiendo el set
      // siguiente); las llamadas con `lote_id` le suman ítems a esa card; la que trae
      // `cerrar: true` la cierra y recién ahí espera el click, registrando el resolver
      // bajo el id de la CARD (no de este tool_use) porque es la card la que resuelve.
      if (name === 'generar_lote_imagenes' && (input?.lote_id || input?.cerrar === false)) {
        const cerrar = input?.cerrar !== false;
        const nuevos = Array.isArray(input?.items) ? input.items : [];
        let cardId: string = typeof input?.lote_id === 'string' ? input.lote_id : '';
        let existente: V2ToolCard | undefined;
        if (cardId) {
          for (const m of latestMessagesRef.current) {
            existente = m.toolCards?.find((c) => c.id === cardId && c.kind === 'generar_lote_imagenes');
            if (existente) break;
          }
          if (!existente || existente.status !== 'pending' || existente.input?.abierto !== true) {
            return Promise.resolve({
              __texto: `No hay ningún lote abierto con lote_id "${cardId}" (ya se cerró, se generó o no existe). Abrí uno nuevo con cerrar: false.`,
              __error: true,
            });
          }
        }
        const itemsPrevios: any[] = Array.isArray(existente?.input?.items) ? existente!.input.items : [];
        const items = [...itemsPrevios, ...nuevos];
        const imagenes = items.reduce(
          (acc: number, it: any) => acc + Math.max(1, Math.min(6, Math.floor(Number(it?.cantidad) || 1))),
          0,
        );
        const inputNuevo = {
          ...(existente?.input ?? {}),
          ...(existente ? {} : input),
          items,
          abierto: !cerrar,
        };
        delete (inputNuevo as any).lote_id;
        delete (inputNuevo as any).cerrar;
        if (!existente) {
          cardId = toolUseId;
          appendToolCard({ id: cardId, kind: 'generar_lote_imagenes', input: inputNuevo, status: 'pending' });
        } else {
          updateToolCard(cardId, { input: inputNuevo });
        }
        const tally = { lote_id: cardId, items: items.length, imagenes };
        if (!cerrar) {
          // Imperativo y con la llamada exacta: después de un tool_result el modelo tiende a
          // NARRAR ("voy con el segundo set") en vez de llamar, y un set narrado no existe.
          return Promise.resolve({
            __texto: `Lote abierto (lote_id="${cardId}"): ${items.length} ítem(s), ${imagenes} imagen(es) hasta ahora. El usuario todavía NO ve ningún botón. Tu PRÓXIMA acción es otra llamada a generar_lote_imagenes con lote_id="${cardId}" y el siguiente set; la última lleva cerrar: true. No le escribas al usuario entre sets ni des el lote por terminado: si no llamás, los ítems que faltan no existen.`,
            ...tally,
          });
        }
        return new Promise((resolve) => {
          cardResolversRef.current.set(cardId, (response: any) => {
            if (!response?.detached) {
              updateToolCard(cardId, {
                status: response?.cancelled ? 'cancelled' : 'resolved',
                response,
              });
            }
            resolve(response);
            cardResolversRef.current.delete(cardId);
          });
        });
      }

      const kind: V2ToolCardKind = name;
      const card: V2ToolCard = {
        id: toolUseId,
        kind,
        input,
        status: 'pending',
      };
      appendToolCard(card);
      return new Promise((resolve) => {
        cardResolversRef.current.set(toolUseId, (response: any) => {
          // `{detached:true}` (el usuario escribió otra cosa y soltamos el
          // turno): la card NO se toca — sigue 'pending' y clickeable, que es
          // justamente lo que se quiere. Solo liberamos el await del loop.
          // `{cancelled:true}` (stopTurn / reset / loadChatById) → la card
          // queda 'cancelled' (resumen deshabilitado), no 'resolved'.
          if (!response?.detached) {
            updateToolCard(toolUseId, {
              status: response?.cancelled ? 'cancelled' : 'resolved',
              response,
            });
          }
          // Al modelo la cancelación le llega con su significado, no como un JSON mudo:
          // con `{cancelled:true}` a secas volvía a proponer la misma card con otras
          // palabras (ADR-2026-09-18-un-no-es-un-no).
          resolve(
            response?.cancelled && typeof response.__texto !== 'string'
              ? { ...response, __texto: 'El usuario DESCARTÓ esta card sin generar. No la repitas igual: decí en una línea qué vas a hacer distinto, o preguntá qué quiere. Si pide "el mismo prompt pero con X", copiá el prompt de esta card LITERAL y cambiá sólo X.' }
              : response,
          );
          cardResolversRef.current.delete(toolUseId);
        });
      });
    },
    [appendToolCard, updateToolCard, setResearchEnabled, adjuntarAlUltimoAssistant],
  );

  const resolveToolCard = useCallback(
    (cardId: string, response: any) => {
      const resolver = cardResolversRef.current.get(cardId);
      if (resolver) {
        // Solo permitimos resolver la card que está activa en el momento —
        // las que están en cola se resuelven recién cuando llegan al frente.
        // Si el caller intenta resolver una que no es la activa, ignoramos.
        if (activeToolCard?.id !== cardId) return;
        resolver(response);
        return;
      }
      // Sin resolver = el turno que la creó ya se soltó (`detachTurn`, porque
      // el usuario siguió escribiendo). La card SIGUE siendo utilizable por su
      // cuenta: genera igual, solo que el resultado ya no vuelve al agente.
      // Sin esto se quedaba colgada en "generando" para siempre — la
      // generación corre pero nadie le cambia el status.
      updateToolCard(cardId, {
        status: response?.cancelled ? 'cancelled' : 'resolved',
        response,
      });
    },
    [activeToolCard, updateToolCard],
  );

  // Suelta el turno en curso SIN matar las cards: el usuario decidió escribir
  // otra cosa en vez de responder la propuesta. El await del loop se libera
  // (y el genToken bumpeado hace que el turno viejo se descarte), pero la
  // card queda 'pending' y se puede generar más tarde.
  const detachTurn = useCallback(() => {
    generationRef.current += 1;
    try {
      turnAbortRef.current?.abort();
    } catch {
      /* noop */
    }
    for (const [cardId, resolver] of cardResolversRef.current.entries()) {
      // La marca va ANTES de resolver: el resolver no toca el status cuando
      // ve `detached`, así que si no la ponemos acá la card queda pending
      // "normal" y se convierte en la activa para siempre.
      updateToolCard(cardId, { detached: true });
      resolver({ detached: true });
    }
    cardResolversRef.current.clear();
    setStreamingBoth(false);
    updateToolActivity(null);
  }, [updateToolActivity, setStreamingBoth, updateToolCard]);

  // Corta el turno en curso (botón Detener): aborta el stream, cancela las
  // cards pendientes y libera el input. Mismo escape que reset() pero sin
  // borrar el chat. También lo dispara indirectamente el watchdog (vía
  // abort) — acá el corte es explícito del usuario, así que no mostramos
  // mensaje de error (el genToken bumpeado hace que el catch lo saltee).
  const stopTurn = useCallback(() => {
    generationRef.current += 1;
    try {
      turnAbortRef.current?.abort();
    } catch {
      /* noop */
    }
    for (const resolver of cardResolversRef.current.values()) {
      resolver({ cancelled: true });
    }
    cardResolversRef.current.clear();
    setStreamingBoth(false);
    updateToolActivity(null);
  }, [updateToolActivity]);

  // ── Send message (main loop) ──────────────────────────────────────
  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      // Ref, no state: ver `streamingRef`.
      if (!trimmed || streamingRef.current) return;
      pedidoRef.current = trimmed;
      turnoRef.current += 1;
      skillsDeLaRondaRef.current = [];

      // Guard de créditos: no disparamos el request si el saldo no cubre el
      // costo potencial del turno (Opus puede gastar varios créditos). Leemos
      // el saldo fresco y comparamos contra el mínimo del modelo elegido.
      // Con Gemini Flash (nivel Baja) el mínimo es 0 → ni consultamos el
      // saldo: chatear es gratis (ADR-608).
      const minNeeded = minCreditsForModel(modelRef.current);
      const cid = companyIdRef.current;
      if (cid && minNeeded > 0) {
        const { data: cp } = await supabase
          .from('company_profiles')
          .select('imagenes_restantes:imagenes_disponibles')
          .eq('id_company', cid)
          .single();
        const left = Number((cp as any)?.imagenes_restantes ?? 0);
        if (left < minNeeded) {
          // Sin créditos ni para el modelo más barato → container de mejorar
          // plan (mismo modal de créditos agotados de la app). Si tiene saldo
          // pero no alcanza para Alta, solo sugerimos bajar a Media.
          const outOfCredits = left < minCreditsForModel(MODEL_MEDIA);
          setMessages((prev) =>
            outOfCredits
              ? [...prev, { role: 'user', text: trimmed }, { role: 'assistant', text: '', kind: 'upgrade' }]
              : [
                  ...prev,
                  { role: 'user', text: trimmed },
                  {
                    role: 'assistant',
                    text: 'No te alcanzan los créditos para una consulta en este nivel. Cambiá la inteligencia a Baja (gratis) o sumá créditos.',
                  },
                ],
          );
          return;
        }
      }

      // Snapshot del historial antes de agregar el user turn, mapeado al
      // formato de la API (solo content de texto por ahora).
      const apiMessages: Array<{ role: 'user' | 'assistant'; content: any }> = [];
      // De qué mensaje de `messages` salió cada entrada de `apiMessages`. Lo necesita la
      // compactación: el historial se rearma entero en cada turno, así que el resumen se
      // guarda apuntando a `messages` (que es estable) y no a `apiMessages`
      // (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar).
      const origenDeApi: number[] = [];
      // Los registros de los turnos del asistente (ids de lo generado, qué mostró) NO se
      // pegan a su texto: van como nota del sistema al principio del mensaje siguiente
      // del usuario. Pegados al texto del asistente, el modelo los tomaba por suyos y los
      // imitaba con ids inventados (ADR-2026-09-13-los-registros-no-son-del-modelo).
      let registrosPendientes: string[] = [];
      for (let iMensaje = 0; iMensaje < messages.length; iMensaje++) {
        const m = messages[iMensaje];
        if (m.role === 'user') {
          // El texto va limpio (es el de la burbuja) más un marcador con lo que adjuntó:
          // sin él, "generá con ese producto" dos turnos después no tenía de dónde salir
          // (ADR-2026-09-13-los-adjuntos-viejos-se-recuerdan).
          apiMessages.push({
            role: 'user',
            content: notaDeSistemaParaElUsuario(registrosPendientes) + m.text + marcadorDeAdjuntos(m.adjuntos, iMensaje),
          });
          origenDeApi.push(iMensaje);
          registrosPendientes = [];
        } else if (m.role === 'assistant') {
          // En el historial de API ignoramos las toolCards "resumidas" por
          // simplicidad — la API ya recibió el tool_use + tool_result en la
          // ronda original. Metemos el texto MÁS un marcador compacto por
          // cada generación resuelta: sin él, el agente pierde los ids de lo
          // generado (imágenes / frames de video) en los turnos siguientes y
          // no puede reusarlos con source "generada" (re-ediciones, "animá
          // esa imagen", etc.).
          // `text` junta SOLO los marcadores; el texto visible va aparte, como lo escribió.
          const visible = m.text || '';
          let text = '';
          // Un gráfico ya mostrado viaja como marcador con el título: el HTML entero en
          // cada turno multiplicaría el contexto sin aportar nada (regla del Chat).
          for (const g of m.graficos ?? []) {
            text += `\n[mostraste una respuesta gráfica: "${clipForLog(g.titulo, 80)}"]`;
          }
          // Lo que usó sin card (miró, leyó, cargó, lo que el usuario eligió), con ids.
          for (const r of m.registros ?? []) text += `\n${r}`;
          for (const c of m.toolCards || []) {
            // Imagen y video: resueltas Y descartadas, con el prompt entero — es lo que
            // hace posible "el mismo prompt pero con X" turnos después
            // (ADR-2026-09-26-el-mismo-prompt-se-copia-del-registro).
            const registroDeGeneracion = registroDeGeneracionParaHistorial(c);
            if (registroDeGeneracion) {
              text += `\n${registroDeGeneracion}`;
              continue;
            }
            if (c.status !== 'resolved') continue;
            if (c.kind === 'generate_audio' && c.response?.audio_url) {
              const inp = c.input || {};
              text += `\n[registro: audio generado OK — tipo=${inp.audio_type || 'voz'}${inp.text ? `, texto="${clipForLog(inp.text, 250)}"` : ''}]`;
            } else if (
              c.kind === 'generar_lote_imagenes' &&
              Array.isArray(c.response?.por_item)
            ) {
              // El lote deja por ítem su product_id y sus image_ids: es lo que permite
              // "agarrá 5 de esos y hacelos en azul" turnos después
              // (ADR-2026-09-08-el-chat-trabaja-en-lote).
              text += `\n${registroLoteParaHistorial(c.response as ResumenLote)}`;
            } else if (
              c.kind === 'agregar_productos' &&
              Array.isArray(c.response?.productos)
            ) {
              text += `\n${registroProductosParaHistorial(c.response as ResumenCreacion)}`;
            } else if (c.kind === 'crear_pdf' && c.response?.created) {
              text += `\n${registroPdfParaHistorial(c.response.pdf as PdfCreado)}`;
            } else if (c.kind === 'crear_planilla' && c.response?.created) {
              text += `\n${registroPlanillaParaHistorial(
                c.response.planilla as PlanillaCreada,
              )}`;
            } else if (
              c.kind === 'mostrar_guardado' &&
              Array.isArray(c.response?.items)
            ) {
              // Qué le mostró y en qué orden, para que "el segundo" tenga id
              // (ADR-2026-09-08-mostrar-lo-guardado).
              text += `\n${registroGuardadoParaHistorial(
                c.response.tipo as TipoGuardado,
                c.response.items as ItemGuardado[],
              )}`;
            } else if (c.kind === 'preguntar') {
              // Las respuestas viven en el tool_result, que no se re-manda: sin esto el
              // agente olvida al turno siguiente lo que el usuario eligió
              // (ADR-2026-09-12-preguntar-es-global-y-el-chat-no-la-duplica).
              const reg = registroPreguntasParaHistorial(c.response);
              if (reg) text += `\n${reg}`;
            } else if (
              c.kind === 'show_recent_media' &&
              Array.isArray(c.response?.media) &&
              c.response.media.length > 0
            ) {
              // Le pasamos al agente QUÉ mostró y en qué ORDEN, para que pueda
              // mapear "la primera/segunda/tercera/última" a un image_id y
              // usarlo (source "generada") — sin pedirle el id al usuario.
              const list = (c.response.media as any[])
                .map(
                  (mm, i) =>
                    `#${i + 1} image_id=${mm.id}${mm.isVideo ? ' [video]' : ''}${mm.label ? ` — "${mm.label}"` : ''}`,
                )
                .join('; ');
              text += `\n[registro: le mostré sus últimas generaciones EN ORDEN (con su descripción): ${list}. Si el usuario se refiere a una — por POSICIÓN ("la tercera/última/esa") o por DESCRIPCIÓN ("la del vestido verde", "la de la playa") — matcheala a ESE image_id (por el orden o por la descripción) y usala (ej. animar → first_frame {source:"generada", image_id}). NUNCA le pidas el id.]`;
            }
          }
          if (visible) {
            apiMessages.push({ role: 'assistant', content: visible });
            origenDeApi.push(iMensaje);
          }
          if (text.trim()) registrosPendientes.push(text.trim());
        }
      }
      // ── Adjuntos del menú "+" ──────────────────────────────────────
      // Inyectamos un bloque de contexto (solo en el content de la API, NO
      // en el texto visible) para que el agente use producto/modelo ya
      // elegidos y sepa que hay imagen de referencia. Las imágenes de
      // referencia se re-firman y quedan en referenceImagesRef para que la
      // card de generación libre las pase como reference_images.
      const atts = attachmentsRef.current;
      let contextNote = '';
      // `lines` vive AFUERA del if: además de los adjuntos, puede recibir la
      // pista del toggle Imagen/Video, que existe haya o no adjuntos.
      const lines: string[] = [];
      referenceImagesRef.current = [];
      // Archivos de texto del Chat: bloques de texto con el nombre adelante, ANTES de
      // la consigna (el modelo lee el archivo y después lo que se le pide).
      const textoBlocks: any[] = [];
      // Las imágenes ADJUNTAS van como bloques pelados; las REFERENCIAS del catálogo
      // (pose / fondo) van etiquetadas, para que el modelo sepa cuál es cuál
      // (ADR-2026-09-08-adjuntar-desde-el-catalogo-de-la-marca). Las dos entran a
      // `referenceImagesRef` — o sea, a `reference_images` de la generación.
      const urlsAdjuntas: string[] = [];
      const referenciaBlocks: any[] = [];
      if (atts.length > 0) {
        const products = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'product' }> =>
            a.kind === 'product',
        );
        const modelosAdjuntos = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'model' }> =>
            a.kind === 'model',
        );
        const images = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'image' }> =>
            a.kind === 'image',
        );
        const textos = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'texto' }> =>
            a.kind === 'texto',
        );
        const referencias = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'referencia' }> =>
            a.kind === 'referencia',
        );
        if (textos.length > 0) {
          lines.push(
            `El usuario adjuntó ${textos.length} archivo(s) de texto — van como bloques de texto en este mismo mensaje, cada uno con su nombre adelante. Leelos y usalos para responder.`,
          );
          for (const t of textos) {
            textoBlocks.push({
              type: 'text',
              text: `[Archivo adjunto: ${t.name}]\n${t.text}`,
            });
          }
        }
        const planillas = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'planilla' }> => a.kind === 'planilla',
        );
        if (planillas.length > 0) {
          lines.push(
            `El usuario adjuntó ${planillas.length} planilla(s). NO viajan enteras: abajo va su FORMA y una muestra de filas. Para leer más filas usá ver_planilla${
              planillas.length > 1 ? ', diciendo en `archivo` CUÁL de ellas querés' : ''
            }; para devolverle una nueva (o la misma con cambios) usá crear_planilla.`,
          );
          for (const p of planillas) {
            textoBlocks.push({ type: 'text', text: p.resumen });
          }
        }
        const pdfs = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'pdf' }> => a.kind === 'pdf',
        );
        if (pdfs.length > 0) {
          lines.push(
            `El usuario adjuntó ${pdfs.length} PDF. Abajo va su forma y lo que se pudo leer. Para más páginas usá ver_pdf${
              pdfs.length > 1 ? ', diciendo en `archivo` CUÁL de ellos querés' : ''
            }; para devolverle un documento nuevo, crear_pdf.`,
          );
          for (const p of pdfs) textoBlocks.push({ type: 'text', text: p.resumen });
        }
        // El video NO viaja ni se ve: Claude no recibe video. Queda su path en el mensaje
        // y `ver_video` se lo manda a Gemini (ADR-2026-09-11-el-agente-entiende-videos-con-gemini).
        const videos = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'video' }> => a.kind === 'video',
        );
        if (videos.length > 0) {
          lines.push(
            `El usuario adjuntó ${videos.length} video(s): ${videos
              .map((v) => `"${v.name}"${v.duracionSeg ? ` (${formatearDuracion(v.duracionSeg)})` : ''}`)
              .join(', ')}. NO lo podés ver: para entenderlo llamá ver_video${
              videos.length > 1 ? ', diciendo en `archivo` cuál' : ''
            } (cuesta poco y tarda medio minuto: avisale en una línea que lo estás analizando). Si pide algo sobre ese video (editarlo, cambiarle algo, más tomas, copiarlo), cargá la skill copiar-un-video: el video viaja como reference_video de generate_video, nunca se regenera desde sus cuadros.`,
          );
          // Lo que el modelo de video rechazaría sin arreglo (proporción, menos de 2 s): el
          // usuario ya lo ve en un recuadro; el agente no tiene que intentarlo igual
          // (ADR-2026-10-20-el-video-de-referencia-se-ajusta-a-lo-que-acepta-seedance).
          for (const v of videos) {
            const aviso = avisoDeVideoDeReferencia(v);
            if (aviso?.grave) {
              lines.push(
                `"${v.name}" NO sirve como reference_video de generate_video (el modelo lo rechaza): ${aviso.texto} Podés mirarlo con ver_video, pero no generes con él; si te pide un video a partir de ese, explicale esto en una línea.`,
              );
            }
          }
        }
        if (products.length > 0) {
          lines.push(
            `Producto(s) que el usuario ya adjuntó/eligió (certeza total) — pasá estos IDs en reference_images de generate_image (source "producto") y NO muestres pick_product: ${products
              .map((p) => `"${p.name}" (id=${p.productId})`)
              .join(', ')}`,
          );
        }
        if (modelosAdjuntos.length === 1) {
          const model = modelosAdjuntos[0];
          // Con el ID, no sólo el nombre: con "Ruperta" y "Ruperta pro" en la lista, el
          // nombre solo hizo que el agente generara cuatro veces con la otra
          // (ADR-2026-09-13-los-adjuntos-dicen-su-id).
          const modelId = modelIdDeAdjunto(model);
          lines.push(
            `Modelo que el usuario ya adjuntó/eligió: "${model.name}"${modelId ? ` (model_id=${modelId})` : ''}. Usalo: pasalo en reference_images de generate_image (source "modelo"${modelId ? ` + model_id=${modelId}, EXACTAMENTE ese id aunque en tu lista haya otro de nombre parecido` : ''}) y no le pidas que vuelva a elegir modelo.`,
          );
        } else if (modelosAdjuntos.length > 1) {
          // Varios modelos: son ÉSTOS y no otros del catálogo. Cómo repartirlos lo dice
          // el pedido ("uno por producto", "la misma en todas"); si no lo dice, se
          // pregunta en texto, no se elige un tercero (ADR-2026-09-13-varios-modelos-en-la-caja).
          const lista = modelosAdjuntos
            .map((m) => {
              const id = modelIdDeAdjunto(m);
              return `"${m.name}"${id ? ` (model_id=${id})` : ''}`;
            })
            .join(', ');
          lines.push(
            `El usuario adjuntó ${modelosAdjuntos.length} modelos: ${lista}. Son ÉSTOS los que van en las fotos (source "modelo" + model_id EXACTO de cada uno), no otros del catálogo. Repartilos como pidió ("uno por producto" = cada producto con UNO de estos, alternando; "la misma en todas" = uno solo); si no dijo cómo, preguntá en una línea antes de armar el lote.`,
          );
        }
        if (images.length > 0) {
          lines.push(
            `El usuario adjuntó ${images.length} imagen(es) — las podés VER en este mismo mensaje (van como bloques de imagen). Miralas y usalas como referencia visual con generate_image. (El sistema además se las pasa a la generación como reference_images.) Si lo adjuntado es una prenda que va PUESTA sobre alguien, el prompt tiene que pedir explícitamente una persona real, nunca un maniquí.`,
          );
          // Una CREACIÓN adjunta tiene id: sin decírselo, para "la misma foto pero con
          // otra modelo" el agente abría el selector y elegía otra creación
          // (ADR-2026-09-13-los-adjuntos-dicen-su-id).
          const creaciones = images
            .map((a) => imageIdDeCreacion(a))
            .filter((id): id is string => !!id);
          if (creaciones.length > 0) {
            lines.push(
              `De esas imágenes, ${creaciones.length === 1 ? 'una es una CREACIÓN suya' : `${creaciones.length} son CREACIONES suyas`} (image_id=${creaciones.join(', ')}). Si pide "la misma foto pero con otra modelo / otro producto / otro fondo", esa creación es LA referencia de la escena: pasala en reference_images con source "generada" + ese image_id (no busques otra con pick_creacion), sumá el modelo o producto nuevo con su id, y en el prompt decí qué se MANTIENE de la referencia (escena, encuadre, luz, la prenda si no cambia) y qué CAMBIA.`,
            );
          }
          const urls = await Promise.all(
            images.map((a) => signReferenceImage(a.storagePath, a.bucket)),
          );
          urlsAdjuntas.push(...urls.filter((u): u is string => !!u));
        }
        // Un MOODBOARD entero es un puntero, como el lote: el agente lo mira con
        // ver_moodboard (ADR-2026-09-13-el-agente-ve-los-moodboards).
        const carpetas = atts.filter(
          (a): a is Extract<ChatAttachment, { kind: 'moodboard' }> => a.kind === 'moodboard',
        );
        if (carpetas.length > 0) lines.push(lineaDeContextoDeMoodboards(carpetas));
        if (referencias.length > 0) {
          // Puro y con tests (ADR-2026-09-14-la-pose-adjunta-viaja-con-su-id-y-no-se-describe):
          // el texto de la pose decía "describila en el prompt" escondido acá adentro y por eso
          // sobrevivió a los dos arreglos que sacaron esa instrucción de la skill.
          for (const linea of lineasDeReferenciasAdjuntas(referencias)) lines.push(linea);
          // Fotos de un MOODBOARD (carpetas de referencias de la marca —
          // ADR-2026-09-13-los-moodboards-entran-a-la-biblioteca-del-agente): son
          // referencias de ESTÉTICA, no de producto ni de persona. El `name` es el de
          // la carpeta, así el modelo agrupa las que vienen de la misma.
          const moodboards = referencias.filter((r) => r.tipo === 'moodboard');
          if (moodboards.length > 0) {
            const carpetas = [...new Set(moodboards.map((m) => m.name))];
            lines.push(
              `${moodboards.length} foto(s) de REFERENCIA DE ESTILO de ${carpetas.length > 1 ? 'los estilos' : 'el estilo'} ${carpetas.map((c) => `"${c}"`).join(', ')} del usuario. Las VES en este mensaje, etiquetadas [ESTILO: ...]. Son la ESTÉTICA que quiere: luz, encuadre, pose, paleta, locación, actitud, tratamiento de color. Describí eso en el prompt (además de que el sistema las pase como referencia). NO copies las personas, la ropa ni los productos de esas fotos: la persona sale del modelo y el producto de su catálogo. Cargá la skill \`moodboard\` antes de generar (dice qué tomar de cada foto, cuándo pasar una como referencia y cuándo armar primero la escena). Si no adjuntó producto y pide "algo así", preguntale con cuál.`,
            );
          }
          const ETIQUETA_REFERENCIA = { pose: 'POSE', fondo: 'FONDO', moodboard: 'ESTILO', elemento: 'ELEMENTO DE MARCA', tipografia: 'TIPOGRAFÍA' } as const;
          for (const r of referencias) {
            if (!r.imageUrl) continue;
            referenciaBlocks.push({
              type: 'text',
              text: `[${ETIQUETA_REFERENCIA[r.tipo]}: "${r.name}"]`,
            });
            referenciaBlocks.push({
              type: 'image',
              source: { type: 'url', url: r.imageUrl },
            });
          }
        }
        // Lo que la generación va a recibir como `reference_images`, etiquetado: quien
        // la arme suma el producto y el modelo y recorta por prioridad.
        const conImagen = referencias.filter((r) => !!r.imageUrl);
        referenceImagesRef.current = [
          ...urlsAdjuntas.map((url) => ({ url, tipo: 'adjunto' as const })),
          // Los fondos guardados vienen con firma de una hora tomada al cargar el
          // catálogo: se renueva acá, que es cuando de verdad va a usarse
          // (ADR-2026-09-12-las-fotos-viajan-con-firma-fresca).
          ...(await conFirmasFrescas(conImagen.map((r) => r.imageUrl))).map((url, i) => ({
            url,
            // Al motor, con el tipo que su enum conoce (moodboard → adjunto).
            tipo: tipoDeReferenciaParaElMotor(conImagen[i].tipo),
            // La tipografía, con su rótulo: sin él es una "Imagen de referencia" más.
            ...(etiquetaDeReferenciaParaElMotor(conImagen[i]) ? { etiqueta: etiquetaDeReferenciaParaElMotor(conImagen[i]) } : {}),
          })),
        ];
      }

      // Pista del toggle Imagen/Video del modo "Crear desde cero" (solo con
      // Ayuda de IA prendida). Es orientación, no imposición: el agente
      // puede seguir conversando y ajustando.
      if (outputKindHintRef.current) {
        lines.push(buildOutputKindHintLine(outputKindHintRef.current));
      }
      // Lo que el que monta el hook quiera sumar al bloque (el lote de fotos del Chat).
      for (const linea of opcionesRef.current.contextoAdjunto?.() ?? []) {
        if (linea) lines.push(linea);
      }

      // Las menciones con `@` del texto nombran adjuntos de este mensaje
      // (ADR-2026-09-21-la-arroba-menciona-los-adjuntos): se le dice cuáles, para que
      // "@Imagen 2" o "@Remera oversize" no se lean como texto suelto.
      const mencionadas = mencionesEn(trimmed, atts);
      if (mencionadas.length > 0) {
        lines.push(
          `Las menciones con @ del texto (${mencionadas.map((n) => `@${n}`).join(', ')}) nombran adjuntos de ESTE mensaje: el producto, modelo, pose, fondo o archivo con ese nombre; "@Imagen N" es la N-ésima imagen adjunta, en el orden de los bloques de imagen.`,
        );
      }
      if (lines.length > 0) {
        contextNote = `\n\n[CONTEXTO ADJUNTO POR EL USUARIO (no lo repitas literal, actuá en consecuencia):\n- ${lines.join(
          '\n- ',
        )}]`;
      }

      // Si el TURNO ANTERIOR del asistente mostró la galería de últimas
      // generaciones (`show_recent_media`), le adjuntamos ESAS imágenes a este
      // mensaje para que el agente las VEA e identifique la que el usuario
      // describa/elija (por más que la descripción sea puramente visual). Solo
      // el turno inmediatamente siguiente (bounded: buscamos el ÚLTIMO mensaje
      // de asistente). Videos no se pasan (no hay bloque de imagen para ellos).
      const recentMediaBlocks: any[] = [];
      for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.role !== 'assistant') continue;
        const galeria = m.toolCards?.find(
          (c) =>
            c.kind === 'show_recent_media' &&
            c.status === 'resolved' &&
            Array.isArray(c.response?.media),
        );
        if (galeria) {
          const imgs = (galeria.response!.media as any[]).filter(
            (mm) => !mm.isVideo && mm.thumbUrl,
          );
          if (imgs.length > 0) {
            recentMediaBlocks.push({
              type: 'text',
              text: '[Estas son las últimas generaciones que te mostré, EN ORDEN. Miralas para identificar la que el usuario pida (por descripción visual, prompt o posición) y usá su image_id. NUNCA le pidas el id.]',
            });
            imgs.forEach((mm, idx) => {
              recentMediaBlocks.push({
                type: 'text',
                text: `#${idx + 1} image_id=${mm.id}:`,
              });
              recentMediaBlocks.push({
                type: 'image',
                source: { type: 'url', url: mm.thumbUrl },
              });
            });
          }
        }
        break; // solo el último turno del asistente (bounded)
      }

      // Si hay imágenes de referencia adjuntas o de la galería, mandamos el
      // mensaje como un ARRAY de bloques (imágenes + texto) para que el AGENTE
      // las VEA. Mismo patrón que el Director de Estudio (VideoEditor): el edge
      // function `chat-agent` ya soporta `source: { type: 'url' }` con URLs
      // firmadas. Sin imágenes → texto plano.
      const imageBlocks = [
        ...textoBlocks,
        ...urlsAdjuntas.map((url) => ({
          type: 'image',
          source: { type: 'url', url },
        })),
        // Las del catálogo van DESPUÉS y con su etiqueta ([POSE: …] / [FONDO: …]).
        ...referenciaBlocks,
        ...recentMediaBlocks,
      ];
      // Lo que pasó en el último turno del asistente entra acá, al principio del mensaje.
      const textoDelTurno = notaDeSistemaParaElUsuario(registrosPendientes) + trimmed + contextNote;
      const userContent: any =
        imageBlocks.length > 0
          ? [...imageBlocks, { type: 'text', text: textoDelTurno }]
          : textoDelTurno;
      apiMessages.push({ role: 'user', content: userContent });
      origenDeApi.push(messages.length);

      // El resumen de los adjuntos queda EN el mensaje: se dibuja en la burbuja (que es
      // lo que buscaba ADR-2026-09-08-los-adjuntos-quedan-en-la-burbuja) y una
      // herramienta posterior lo encuentra (ADR-2026-09-08-agregar-productos-es-una-herramienta).
      const adjuntosDelMensaje = [
        ...resumirAdjuntos(atts),
        ...(opcionesRef.current.adjuntosDelMensaje?.() ?? []),
      ];
      setMessages((prev) => [
        ...prev,
        {
          role: 'user',
          text: trimmed,
          ...(adjuntosDelMensaje.length > 0 ? { adjuntos: adjuntosDelMensaje } : {}),
        },
      ]);
      setStreamingBoth(true);
      // Los chips ya se "consumieron" en este envío.
      if (atts.length > 0) setAttachments([]);
      const genToken = generationRef.current;

      // El try cubre TODO el setup (no solo el loop de rondas): así cualquier
      // throw o timeout en la preparación (crear chat, cargar contexto,
      // getSession, armar el prompt) cae al catch y el `finally` SIEMPRE
      // apaga el streaming. Antes el setup corría fuera del try → un cuelgue
      // ahí dejaba "Pensando" para siempre. Ver ADR-141.
      try {
        // Crear la fila del chat ANTES de pegarle al agente para poder mandar
        // `agent_chat_id` y que el edge function impute ahí el costo del agente.
        // Con timeout: si `createChat` se cuelga, no bloquea el turno entero.
        await withTimeout(ensureChatCreated(trimmed), 15_000, 'ensureChatCreated');

      // Last format usado por el user (persistido entre sesiones).
      const lastFormat =
        (() => {
          try {
            return localStorage.getItem('atlas-v2-last-format');
          } catch {
            return null;
          }
        })() || null;

      // Contexto persistente: memorias guardadas + marca activa. Se leen
      // fresco en cada envío (incluye lo que se guardó en turnos previos) y
      // se inyectan al system prompt. Si fallan, seguimos sin ellas.
      let memories: AgentMemory[] = [];
      let brand: BrandContext | null = null;
      let models: ModelV2[] = [];
      try {
        const cid = companyIdRef.current;
        // withTimeout: si alguna carga de contexto se cuelga, NO bloquea el
        // turno — el agente sigue sin ese contexto (el catch usa los defaults).
        let skillsEmpresa: SkillDelAgente[] = [];
        [memories, brand, models, skillsEmpresa] = await withTimeout(
          Promise.all([
            cid ? fetchMemories(cid) : Promise.resolve([]),
            cid ? fetchBrandContext(userIdRef.current, cid) : Promise.resolve(null),
            cid ? fetchUserModels(cid) : Promise.resolve([]),
            cid ? fetchSkillsDeEmpresa(cid) : Promise.resolve([]),
          ]),
          15_000,
          'contexto',
        );
        skillsEmpresaRef.current = skillsEmpresa;
      } catch {
        /* noop — el agente funciona igual sin este contexto */
      }

      // ── Compactación del contexto ────────────────────────────────
      // Si el historial ya no entra cómodo en la ventana del modelo, la parte vieja se
      // pliega en un resumen ANTES de mandar. No tira nunca: si el resumen falla, recorta
      // conservando los ids (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar).
      // `messages` no se toca: el usuario sigue viendo su chat entero y `buscar_en_el_chat`
      // sigue teniendo el original para buscar.
      try {
        guardarResumen(
          await compactarHistorial({
            mensajes: apiMessages,
            origen: origenDeApi,
            model: modelRef.current,
            resumenPrevio: resumenRef.current,
            agentChatId: currentChatIdRef.current,
            avisar: updateToolActivity,
          }),
        );
      } catch (e) {
        // Compactar es una mejora, no un requisito: si algo raro pasa acá, el turno sale
        // como salía antes (y si no entra, el modelo lo va a decir y hay reintento).
        console.warn('[useV2Chat] compactación omitida', e);
      }
      const chatCompactado = resumenRef.current;

      // `stable` (template + catálogos) lo cachea la edge function con un
      // breakpoint; `volatile` (memorias + último formato) va después del
      // breakpoint para que sus cambios no invaliden el cache.
      // Lo que el que monta el hook le suma al prompt (el Chat: reglas de gráficos y
      // sugerencias al estable; desde dónde se abrió y qué tiene habilitado al volátil).
      // Con tope: es una mejora del prompt, no un requisito, y corre ANTES de que arranque
      // el watchdog del stream. Una consulta colgada dejaba el chat "pensando" para siempre
      // (review #171, ADR-2026-10-12-review-171-estudio-y-saldo).
      try {
        const antes = opcionesRef.current.antesDelTurno?.();
        if (antes) {
          let tope: ReturnType<typeof setTimeout> | undefined;
          await Promise.race([
            antes,
            new Promise<void>((resolve) => {
              tope = setTimeout(resolve, TOPE_ANTES_DEL_TURNO_MS);
            }),
          ]).finally(() => clearTimeout(tope));
        }
      } catch {
        // Si falla, el turno sigue igual.
      }
      const extra = opcionesRef.current.promptExtra?.();
      const preferencias = opcionesRef.current.preferencias?.() ?? null;
      // Los formatos personalizados de la marca, para que el agente los vea y los use
      // (ADR-2026-10-21-el-agente-ve-crea-y-usa-los-formatos-personalizados). Sólo en el
      // Chat (el que maneja preferencias); si la carga falla, el turno sigue sin el bloque.
      const formatosPersonalizados = preferencias
        ? await formatosPersonalizadosActuales().catch(() => [] as FormatoPersonalizado[])
        : null;
      const { stable: systemPrompt, volatile: volatileBase } = buildV2SystemPrompt({
        preferencias,
        formatosPersonalizados,
        products: productsRef.current,
        lastFormat,
        shortName,
        brand,
        memories,
        models,
        skillsDeEmpresa: skillsEmpresaRef.current,
        extraEstable: extra?.estable,
        extraVolatil: extra?.volatil,
      });

      // El aviso de "este chat es largo" va al volátil (no al MAPA, que es estable y
      // cacheado): sólo aplica a las conversaciones que llegaron a compactarse.
      const systemVolatile = chatCompactado
        ? volatileBase + avisoDeChatCompactado(chatCompactado.mensajesResumidos)
        : volatileBase;

      // Doce y no ocho: un lote de 100 se arma de a sets de 18, y planificar + mirar el
      // catálogo + seis sets + cerrar no entraban en ocho rondas
      // (ADR-2026-09-13-el-lote-del-agente-se-encola-en-el-backend).
      const MAX_ROUNDS = 12;

      // Último recurso del contexto: si el modelo IGUAL dice que el prompt no entra
      // (la estimación de tokens erró, o entraron muchas imágenes en el turno), se
      // recorta a lo bruto y se reintenta una vez, en vez de perder el turno entero
      // (ADR-2026-09-13-el-chat-se-compacta-y-se-puede-buscar). Es el mismo plan B que
      // usa la compactación cuando el resumen falla: se salvan los ids, se pierde el texto
      // y `buscar_en_el_chat` queda para recuperarlo.
      let yaRecorte = false;
      const recorteDeEmergencia = (): boolean => {
        const corte = puntoDeCorte(apiMessages, ventanaDelModelo(modelRef.current) * FRACCION_COLA * 0.5, 4);
        if (corte <= 0) return false;
        const cuantos = origenDeApi[corte] ?? corte;
        aplicarCompactacion(
          apiMessages,
          corte,
          resumenDeEmergencia(transcripcionDelTramo(apiMessages, corte), cuantos),
        );
        origenDeApi.splice(0, corte + 1, origenDeApi[corte]);
        return true;
      };

      // (El `try` abre arriba, antes de ensureChatCreated — así el finally
      //  cubre TODO el setup, no solo el loop de rondas.)
        // Con qué etiqueta arranca la ronda: "Pensando…" la primera; después, lo que el
        // modelo está leyendo de la ronda anterior (ADR-2026-10-15-el-agente-dice-que-esta-leyendo).
        let etiquetaDeLaRonda = ETIQUETA_PENSANDO;
        /** El modelo cerró el turno (una ronda sin herramientas). Si no, se acabaron las rondas. */
        let cerroElTurno = false;
        for (let round = 0; round < MAX_ROUNDS; round++) {
          // Si reset() corrió mientras esperábamos, cortamos sin tocar
          // state — el chat actual ya no es nuestro.
          if (genToken !== generationRef.current) return;

          // Empezamos cada ronda con una burbuja assistant nueva. Sin esto,
          // el texto de la ronda 2 se concatena con el de la ronda 1 y se
          // muestra todo arriba de los tool cards en lugar de quedar
          // intercalado entre las cards (que es lo natural en este flujo).
          setMessages((prev) => {
            indiceDeLaRondaRef.current = prev.length;
            return [...prev, { role: 'assistant', text: '' }];
          });

          // Cada ronda arranca con una etiqueta: después de una tool el texto anterior sigue
          // en pantalla y, sin esto, el usuario no ve que el modelo volvió a trabajar
          // (ADR-2026-09-09-el-chat-dice-que-esta-haciendo). Si la ronda anterior trajo
          // algo para leer (fotos, una skill, la pantalla), dice eso y no "Pensando…"
          // (ADR-2026-10-15-el-agente-dice-que-esta-leyendo). Se apaga con el primer
          // token de texto o se reemplaza por la etiqueta de la tool que arranque.
          updateToolActivity(etiquetaDeLaRonda);

          // getSession con timeout: es un punto de cuelgue conocido de
          // Supabase (si el refresh del token se traba, no resuelve nunca) y
          // pasa ANTES del watchdog del fetch → sin esto, "Pensando" eterno.
          const {
            data: { session },
          } = await withTimeout(
            supabase.auth.getSession(),
            15_000,
            'getSession',
          );
          if (!session?.access_token) throw new Error('No session');

          // Watchdog de inactividad: sin esto, si la conexión muere EN
          // SILENCIO (compu dormida, cambio de red, edge function colgado),
          // `reader.read()` no resuelve nunca → "Pensando" eterno con el
          // input bloqueado. Abortamos si el stream queda mudo demasiado
          // tiempo → cae al catch y el usuario puede reintentar. El edge
          // function manda `: ping` cada 15s (flag `keepalive`), así que el
          // silencio real es corto; mientras corre una tool server-side
          // (scraper, puede callar minutos) damos margen extra por si
          // todavía corre un edge function viejo sin pings.
          const ac = new AbortController();
          turnAbortRef.current = ac;
          let idleTimer: ReturnType<typeof setTimeout> | null = null;
          const armIdleWatchdog = () => {
            if (idleTimer) clearTimeout(idleTimer);
            idleTimer = setTimeout(
              () => ac.abort(),
              servidorEnCursoRef.current ? 240_000 : 90_000,
            );
          };
          const cuerpoDelTurno = {
            messages: apiMessages,
            project_id: projectIdRef.current,
            id_company: companyIdRef.current,
            // El edge function imputa el costo del agente a este chat.
            agent_chat_id: currentChatIdRef.current,
            // Las del catálogo más las que suma el que monta el hook (el Chat).
            tools: [
              ...V2_AGENT_TOOLS,
              // Buscar en el chat sólo tiene sentido cuando hay chat que NO está en
              // contexto. Antes de compactar, dárselo es invitarlo a usarla al pedo.
              ...(chatCompactado ? [HERRAMIENTA_BUSCAR_EN_EL_CHAT] : []),
              ...(opcionesRef.current.herramientasExtra?.() ?? []),
            ],
            system: systemPrompt,
            // Bloque volátil del system (memorias + último formato). La edge
            // function lo agrega DESPUÉS del breakpoint de cache — si falta
            // (front viejo), todo sigue funcionando con `system` solo.
            system_volatile: systemVolatile,
            // Habilita la web search nativa de Anthropic (server-side) para
            // el Asistente. El Director de Estudio no manda este flag.
            web_search: opcionesRef.current.busquedaWeb?.() ?? true,
            // Habilita el MCP de Apify (scrapers IG/FB/Google Places/
            // e-commerce). Server-side vía el connector de Anthropic.
            mcp: true,
            // Modelo según el nivel de inteligencia elegido (Sonnet / Opus).
            model: modelRef.current,
            // Investigación externa (web_search + Apify MCP): el edge function
            // solo la adjunta si esto es true, lo que pasa una vez que el agente
            // llamó `activar_investigacion`. Hasta entonces, contexto liviano.
            research: researchEnabledRef.current,
            // Pide pings `: ping` cada 15s en el SSE para que el watchdog
            // detecte conexión muerta. El plugin de Premiere no lo manda.
            keepalive: true,
            // Dónde está el usuario (ruta), para la métrica del turno: el chat también
            // trabaja adentro del editor de video desde
            // ADR-2026-09-11-el-director-del-estudio-se-funde-en-el-agente.
            ...(opcionesRef.current.pantalla?.() ? { pantalla: opcionesRef.current.pantalla?.() } : {}),
            ...(opcionesRef.current.cuerpoExtra?.() ?? {}),
          };

          // `cuerpoDelTurno` referencia `apiMessages` por identidad: se serializa recién
          // acá, así el recorte de emergencia (que muta el array) alcanza al reintento.
          const pedirTurno = async (): Promise<Response> => {
            // Las imágenes que viajan por URL (fotos de producto de `ver_productos`,
            // fondos, generadas recientes, frames) llevan firmas de una hora tomadas al
            // cargar el catálogo; con la pestaña abierta horas, Anthropic las encontraba
            // vencidas y rechazaba el turno entero. Se renuevan las que están por vencer,
            // en TODO el historial, antes de cada llamada
            // (ADR-2026-09-12-las-fotos-viajan-con-firma-fresca).
            //
            // Y antes de eso, lo que el modelo va a MIRAR pasa del `full/` al `opt/`
            // cuando lo hay: Anthropic no llegaba a bajar un original pesado
            // ("timed out while trying to download the file") y de todos modos lo
            // reescala. La generación sigue recibiendo el `full/` por `reference_images`
            // (ADR-2026-09-14-el-modelo-ve-el-opt-y-el-error-habla-en-castellano).
            await aligerarImagenesEnMensajes(apiMessages);
            await refrescarFirmasEnMensajes(apiMessages);
            armIdleWatchdog();
            return fetch(AGENT_CHAT_URL, {
              method: 'POST',
              signal: ac.signal,
              headers: {
                Authorization: `Bearer ${session.access_token}`,
                apikey: SUPABASE_ANON_KEY,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify(cuerpoDelTurno),
            });
          };

          let res = await pedirTurno();
          if (!res.ok) {
            // El cuerpo trae el motivo (`{ error, detail }` del backend): es lo que
            // permite decirle al usuario QUÉ falló y no "no pude conectarme" para todo
            // (ADR-2026-09-12-el-chat-dice-por-que-fallo-el-turno).
            const err = new ErrorDelAgente(res.status, await res.text().catch(() => ''));
            if (!yaRecorte && esErrorDeContexto(err) && recorteDeEmergencia()) {
              yaRecorte = true;
              updateToolActivity('Acortando la conversación…');
              res = await pedirTurno();
              if (!res.ok) throw new ErrorDelAgente(res.status, await res.text().catch(() => ''));
            } else {
              throw err;
            }
          }
          if (!res.body) throw new ErrorDelAgente(res.status, '');

          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          const blocks = new Map<number, any>();
          let textSoFar = '';
          let stopReason: string | null = null;
          // Un evento `error` del stream (Anthropic saturado a mitad de turno, Gemini que
          // cortó sin contenido): antes se ignoraba y el turno terminaba mudo
          // (ADR-2026-10-12-gemini-vacio-no-es-exito).
          let errorDelStream: ErrorEnElStream | null = null;

          const processEvent = (block: string) => {
            const dataLines: string[] = [];
            for (const line of block.split('\n')) {
              if (line.startsWith('data:'))
                dataLines.push(line.slice(5).trimStart());
            }
            if (dataLines.length === 0) return;
            const raw = dataLines.join('\n');
            if (raw === '[DONE]') return;
            try {
              const evt = JSON.parse(raw);
              if (evt?.type === 'content_block_start') {
                const idx = evt.index;
                const cb = evt.content_block;
                if (cb?.type === 'tool_use') {
                  blocks.set(idx, {
                    type: 'tool_use',
                    id: cb.id,
                    name: cb.name,
                    _json: '',
                  });
                  // El modelo empezó a armar el input de una tool del cliente: puede
                  // ser medio minuto de JSON sin nada visible. Se muestra qué está
                  // armando (ADR-2026-09-09-el-chat-dice-que-esta-haciendo). Reemplaza
                  // a los esqueletos de card por tool que probó esta rama: la etiqueta
                  // dice MÁS (avance incluido) y no promete una UI que puede no llegar.
                  // Va por `etiquetaDeCliente`, que es `etiquetaDeTool` más la etiqueta
                  // propia que la pantalla puede registrar.
                  soltarAccion();
                  updateToolActivity(etiquetaDeCliente(cb.name));
                } else if (cb?.type === 'text') {
                  blocks.set(idx, { type: 'text', text: '' });
                } else if (
                  cb?.type === 'server_tool_use' ||
                  cb?.type === 'mcp_tool_use'
                ) {
                  // El agente arrancó una tool server-side (web search /
                  // scraper) → mostramos el indicador de actividad.
                  servidorEnCursoRef.current = true;
                  soltarAccion();
                  updateToolActivity(etiquetaDeTool(cb.name, cb.type));
                } else if (
                  cb?.type === 'web_search_tool_result' ||
                  cb?.type === 'mcp_tool_result'
                ) {
                  // Llegó el resultado → el modelo vuelve a pensar sobre lo que trajo.
                  servidorEnCursoRef.current = false;
                  updateToolActivity(ETIQUETA_PENSANDO);
                }
                else if (cb?.type === 'thinking') {
                  // En Opus 5.5 el texto que el agente escribe entre tool calls ("Dale,
                  // corrijo el código") vuelve como un bloque `thinking` de progreso; el
                  // backend pide `display: "updates"`, así que su texto es ese aviso y
                  // no el razonamiento. Se trata como texto: se muestra y viaja en el
                  // turno. Con los otros modelos el bloque llega vacío y no deja nada
                  // (ADR-2026-09-26-opus-5-5-en-alta-y-en-las-animaciones).
                  blocks.set(idx, { type: 'text', text: '' });
                }
              } else if (evt?.type === 'content_block_delta') {
                const idx = evt.index;
                const existing = blocks.get(idx);
                if (evt.delta?.type === 'text_delta' || evt.delta?.type === 'thinking_delta') {
                  const piece =
                    (evt.delta.type === 'thinking_delta' ? evt.delta.thinking : evt.delta.text) || '';
                  if (!piece) return;
                  // El agente está escribiendo → ya no hay tool en curso, y lo que hizo antes terminó.
                  soltarAccion();
                  if (toolActivityRef.current) updateToolActivity(null);
                  appendAssistantText(piece);
                  textSoFar += piece;
                  if (existing?.type === 'text') existing.text += piece;
                } else if (
                  evt.delta?.type === 'input_json_delta' &&
                  existing?.type === 'tool_use'
                ) {
                  existing._json += evt.delta.partial_json || '';
                  // Con avance para las que se arman de a partes ("Escribiendo el
                  // prompt 4…", "Armando el Excel… 120 filas"). `updateToolActivity`
                  // ignora la llamada si la etiqueta no cambió.
                  updateToolActivity(
                    opcionesRef.current.etiquetaDeHerramienta?.(existing.name) ??
                      etiquetaConAvance(existing.name, existing._json),
                  );
                }
              } else if (evt?.type === 'content_block_stop') {
                const existing = blocks.get(evt.index);
                if (existing?.type === 'text' && existing.text.includes('[registro:')) {
                  // Un registro escrito por el MODELO es una imitación (con id inventado):
                  // no se muestra ni viaja (ADR-2026-09-13-los-registros-no-son-del-modelo).
                  existing.text = sinRegistrosDelModelo(existing.text);
                  textSoFar = sinRegistrosDelModelo(textSoFar);
                  sanearTextoDelAssistant();
                }
                if (existing?.type === 'tool_use') {
                  try {
                    existing.input = existing._json
                      ? JSON.parse(existing._json)
                      : {};
                  } catch {
                    existing.input = {};
                  }
                  delete existing._json;
                }
              } else if (evt?.type === 'message_delta') {
                if (evt.delta?.stop_reason) stopReason = evt.delta.stop_reason;
              } else if (evt?.type === 'error') {
                errorDelStream = new ErrorEnElStream(evt.error);
              }
            } catch {
              /* ignore malformed */
            }
          };

          while (true) {
            const { done, value } = await reader.read();
            // Cualquier actividad del stream (datos o ping) re-arma el
            // watchdog — solo el silencio total dispara el abort.
            armIdleWatchdog();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            buffer = buffer.replace(/\r\n/g, '\n');
            let boundary = buffer.indexOf('\n\n');
            while (boundary !== -1) {
              const eventBlock = buffer.slice(0, boundary);
              buffer = buffer.slice(boundary + 2);
              if (eventBlock.trim()) processEvent(eventBlock);
              boundary = buffer.indexOf('\n\n');
            }
          }
          if (idleTimer) clearTimeout(idleTimer);
          if (buffer.trim()) processEvent(buffer);

          // Construir assistant content para la próxima ronda.
          const orderedBlocks = [...blocks.entries()]
            .sort((a, b) => a[0] - b[0])
            .map(([, v]) => v);
          const assistantContent: any[] = [];
          for (const b of orderedBlocks) {
            if (b.type === 'text' && b.text) {
              assistantContent.push({ type: 'text', text: b.text });
            } else if (b.type === 'tool_use') {
              assistantContent.push({
                type: 'tool_use',
                id: b.id,
                name: b.name,
                input: b.input || {},
              });
            }
          }

          const toolUses = orderedBlocks.filter((b) => b.type === 'tool_use');
          // El catch lo muestra en la burbuja de esta ronda (si quedó vacía) o en una nueva.
          if (errorDelStream) throw errorDelStream;
          // La PRIMERA ronda sin texto ni tools es un modelo que no contestó: sin esto la
          // burbuja se borraba abajo y el usuario se quedaba mirando su mensaje. Después de
          // una tool, cerrar callado sí es normal (la card ya dijo lo que había que decir).
          if (round === 0 && toolUses.length === 0 && assistantContent.length === 0) {
            throw new ErrorEnElStream({ type: 'turno_vacio', message: MENSAJE_TURNO_VACIO, para_el_usuario: true });
          }
          // Si la burbuja de esta ronda quedó vacía (sin texto ni tools),
          // la sacamos para no dejar gaps visuales en el chat.
          //
          // CRÍTICO: solo si NO hay tools que agregar. Si el agente respondió
          // con una tool y SIN texto (ej. show_recent_media / contact_support,
          // que van directo a la tool), esta burbuja vacía es justo la que va
          // a recibir la card en `executeTool`→`appendToolCard`. Si la
          // borramos acá, appendToolCard no encuentra un mensaje de asistente
          // donde colgar la card y la descarta → la card nunca se renderiza,
          // nunca auto-resuelve, y el turno queda en "Pensando" para siempre.
          // (Root cause del "pensando eterno". Ver ADR-142.)
          if (toolUses.length === 0) {
            setMessages((prev) => {
              if (prev.length === 0) return prev;
              const last = prev[prev.length - 1];
              if (
                last.role === 'assistant' &&
                !last.text &&
                (!last.toolCards || last.toolCards.length === 0)
              ) {
                return prev.slice(0, -1);
              }
              return prev;
            });
          }

          // Si no hay tool calls, terminamos el turno.
          if (stopReason !== 'tool_use' || toolUses.length === 0) {
            cerroElTurno = true;
            break;
          }

          apiMessages.push({ role: 'assistant', content: assistantContent });
          // Las rondas de herramientas pertenecen al turno actual: mantienen alineado
          // `origenDeApi` con `apiMessages` (el recorte de emergencia lo lee).
          origenDeApi.push(messages.length);

          // Schedule TODAS las tools del turno al mismo tiempo: aparecen
          // todas como pending en la queue. La primera se renderiza activa
          // (derivado por useMemo); las demás esperan su turno. El user
          // las resuelve en orden y los `await` continúan independientes.
          const cardPromises = toolUses.map((tu) =>
            // Si la card espera el click o genera sola lo decide el que monta el hook
            // (en el Chat, la preferencia de confirmaciones del usuario), NUNCA el
            // modelo: `autogenerate` no está en el schema de estas tools, pero un modelo
            // puede emitir campos que no le pedimos, así que acá se pisa siempre. Sin la
            // opción, ninguna autogenera — el default histórico. El modo DIRECTO no pasa
            // por este camino (empuja la card con pushToolCard), así que conserva su
            // autogenerado, que es su razón de ser
            // (ADR-2026-09-08-permisos-de-generacion-del-chat).
            executeTool(
              tu.id,
              tu.name,
              TOOLS_AUTOGENERABLES.includes(tu.name)
                ? {
                    ...(tu.input ?? {}),
                    autogenerate: opcionesRef.current.sinConfirmacion?.(tu.name) ?? false,
                  }
                : tu.input,
            ),
          );
          const toolResults: any[] = [];
          for (let i = 0; i < toolUses.length; i++) {
            // La anterior de esta ronda ya terminó: ésta arranca.
            soltarAccion();
            // Mientras corre una tool SIN card (subir las fotos de ver_fotos, renderizar
            // un PDF, guardar una skill) la etiqueta es lo único visible. Con card, la
            // card ya está en pantalla: la etiqueta se apaga para no decir "armando el
            // prompt" arriba de una propuesta que ya lo muestra.
            updateToolActivity(
              TOOLS_SIN_CARD.has(toolUses[i].name)
                ? etiquetaDeTool(toolUses[i].name)
                : (opcionesRef.current.etiquetaDeHerramienta?.(toolUses[i].name) ?? null),
            );
            const response = await cardPromises[i];
            if (genToken !== generationRef.current) return;
            // Queda registrado en el mensaje (con ids, sin imágenes) para los turnos
            // siguientes (ADR-2026-09-13-todo-lo-que-uso-el-agente-queda-registrado).
            const registro = registroDeHerramienta(toolUses[i].name, toolUses[i].input, response);
            if (registro) adjuntarAlUltimoAssistant({ registros: [registro] });
            // La línea de lo que hizo queda pendiente hasta que el agente pase a otra cosa (ver `soltarAccion`).
            if (accionHecha(toolUses[i].name)) {
              accionPendienteRef.current = { indice: indiceDeLaRondaRef.current, nombre: toolUses[i].name };
            }
            toolResults.push({
              type: 'tool_result',
              tool_use_id: toolUses[i].id,
              content:
                typeof response === 'string'
                  ? response
                  : Array.isArray(response?.__bloques)
                    ? response.__bloques
                    : typeof response?.__texto === 'string'
                      ? response.__texto
                      : JSON.stringify(response),
              // Una herramienta de pantalla que falló lo dice con `is_error`: el modelo
              // lee el motivo y ofrece otro camino en vez de dar por hecho el cambio.
              ...(response?.__error === true ? { is_error: true } : {}),
            });
          }
          apiMessages.push({ role: 'user', content: toolResults });
          origenDeApi.push(messages.length);
          etiquetaDeLaRonda = etiquetaAlLeerResultados(toolUses.map((tu) => tu.name));

          // Silenciar el warning de unused para textSoFar fuera del scope.
          void textSoFar;
          // Loop sigue: el agente continúa con los results.
        }
        // Se usaron todas las rondas sin que el modelo cerrara: sin esto el turno terminaba callado,
        // con las burbujas vacías, y el chat parecía trabado (12 rondas de buscar_productos el 22/10).
        // El catch lo muestra donde muestra cualquier error del turno.
        if (!cerroElTurno && genToken === generationRef.current) {
          throw new ErrorEnElStream({ type: 'sin_rondas', message: MENSAJE_SIN_RONDAS, para_el_usuario: true });
        }
      } catch (err) {
        if (genToken !== generationRef.current) return;
        console.error('[useV2Chat] sendMessage error', err);
        const textoDelError = mensajeDeErrorDelTurno(err);
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === 'assistant' && !last.text) {
            next[next.length - 1] = {
              ...last,
              text: textoDelError,
            };
          } else {
            next.push({
              role: 'assistant',
              text: textoDelError,
            });
          }
          return next;
        });
      } finally {
        if (genToken === generationRef.current) {
          // El turno terminó: lo último que hizo, también.
          soltarAccion();
          setStreamingBoth(false);
          updateToolActivity(null);
        } else {
          // El chat cambió (otra conversación, Detener): la línea pendiente no es de la que se ve.
          accionPendienteRef.current = null;
        }
        // El descuento de créditos del turno lo hace el edge function en
        // background (apenas cierra el stream). Avisamos al chrome para que
        // refresque el saldo. El listener del Sidebar re-fetchea ya + a los
        // 2s, así cubre el lag hasta que el descuento commitea.
        try {
          window.dispatchEvent(new Event('atlas:credits-updated'));
        } catch {
          /* noop */
        }
      }
    },
    [
      messages,
      streaming,
      shortName,
      appendAssistantText,
      executeTool,
      ensureChatCreated,
      guardarResumen,
    ],
  );

  // ── Scripted helpers ────────────────────────────────────────────
  const pushUserMessage = useCallback((text: string) => {
    setMessages((prev) => [...prev, { role: 'user', text }]);
  }, []);

  const pushAssistantText = useCallback((text: string) => {
    setMessages((prev) => [...prev, { role: 'assistant', text }]);
  }, []);

  const pushToolCard = useCallback(
    (name: V2ToolCardKind, input: any): Promise<any> => {
      // Si la última burbuja no es assistant, creamos una vacía para
      // colgar la card. Necesario porque executeTool internamente usa
      // appendToolCard que requiere una burbuja assistant existente.
      setMessages((prev) => {
        if (prev.length === 0 || prev[prev.length - 1].role !== 'assistant') {
          return [...prev, { role: 'assistant', text: '' }];
        }
        return prev;
      });
      const id = `scripted-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`;
      return executeTool(id, name, input);
    },
    [executeTool],
  );

  // Carga un chat existente de la DB y lo deja como el actual.
  /**
   * Devuelve al registro en memoria los PDF y planillas que esta conversación (y lo
   * enganchado sin enviar) tenían tomados. Se llama cuando la conversación se VA: chat
   * nuevo o abrir otro del historial. Sin esto el `Map` de módulo —que no se desmonta con
   * nada— sólo crecía, con los documentos de chats que ya nadie puede abrir adentro
   * (ADR-2026-09-09-los-documentos-en-memoria-se-liberan).
   *
   * Se libera lo de ESTE chat y no el registro entero a propósito: el asistente y el Chat
   * montan dos motores distintos sobre el mismo `Map`, y vaciarlo desde uno le sacaría el
   * PDF de abajo al otro.
   */
  const soltarDocumentosDelChat = useCallback(() => {
    const { pdfs, planillas } = documentosEnMemoriaDe(latestMessagesRef.current);
    for (const id of pdfs) liberarPdf(id);
    for (const id of planillas) liberarPlanilla(id);
    for (const a of attachmentsRef.current) {
      if (a.kind === 'pdf') liberarPdf(a.pdfId);
      else if (a.kind === 'planilla') liberarPlanilla(a.planillaId);
    }
  }, []);

  const getChatId = useCallback((): string | null => currentChatIdRef.current, []);

  const llegarAlEstudio = useCallback(async (projectId: string): Promise<DescripcionDePantalla | null> => {
    // Un chat sin fila todavía no necesita atarse: `createChat` lee el proyecto de la
    // ruta al crearse, y la ruta ya va a ser el editor.
    const chatId = currentChatIdRef.current;
    if (chatId) {
      const { asociarChatAProyecto } = await import('./agentChatApi');
      await asociarChatAProyecto(chatId, projectId);
    }
    return (await opcionesRef.current.alLlegarAlEstudio?.(projectId)) ?? null;
  }, []);

  const loadChatById = useCallback(async (chatId: string) => {
    try {
      const { cargarChatConVersion } = await import('./agentChatApi');
      const cargado = await cargarChatConVersion(chatId);
      if (!cargado) return;
      const msgs = cargado.messages;
      // Abortar cualquier turno en vuelo + soltar resolvers viejos.
      generationRef.current += 1;
      // Chat nuevo del historial: arranca sin investigación activa (si la necesita,
      // el agente la vuelve a activar). Evita arrastrar el flag entre chats.
      setResearchEnabled(false);
      for (const resolver of cardResolversRef.current.values()) {
        resolver({ cancelled: true });
      }
      cardResolversRef.current.clear();
      // Mismo criterio que el restore de sessionStorage (ver `restaurarToolCards`).
      const restored = msgs.map((m) => ({
        ...m,
        toolCards: restaurarToolCards(m.toolCards),
      }));
      currentChatIdRef.current = chatId;
      fijarSincronia(chatId, cargado.version, msgs.length, null);
      sesionDeChatRef.current += 1;
      creatingChatRef.current = false;
      // Otro chat, otro historial: el resumen del anterior no aplica (y si este ya es
      // largo, se vuelve a compactar en su primer turno).
      guardarResumen(null);
      try {
        sessionStorage.setItem(claves.chatId,chatId);
        sessionStorage.setItem(claves.mensajes,JSON.stringify(restored));
      } catch {
        /* noop */
      }
      setStreamingBoth(false);
      // El chat que se va se lleva sus documentos en memoria; el que llega no los tiene
      // (los bytes nunca se persistieron) y `ver_pdf` ya sabe decirlo.
      soltarDocumentosDelChat();
      setMessages(restored);
    } catch (e) {
      console.error('[useV2Chat] loadChatById', e);
    }
  }, [soltarDocumentosDelChat, guardarResumen, claves, fijarSincronia]);

  const reset = useCallback(() => {
    // Bump genToken: cualquier loop de sendMessage que estaba esperando una
    // tool response va a chequear esto y abortar limpiamente.
    generationRef.current += 1;
    // Cancelar cualquier resolver pendiente (cards en cola) para que los
    // awaits no queden colgados.
    for (const resolver of cardResolversRef.current.values()) {
      resolver({ cancelled: true });
    }
    cardResolversRef.current.clear();
    soltarDocumentosDelChat();
    setMessages([]);
    guardarResumen(null);
    setStreamingBoth(false);
    updateToolActivity(null);
    setAttachments([]);
    referenceImagesRef.current = [];
    setResearchEnabled(false);
    // Arrancar conversación nueva: soltamos el puntero al chat actual (la
    // fila en `agent_chats` queda como historial, NO se borra) + cancelamos
    // cualquier save pendiente.
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    currentChatIdRef.current = null;
    fijarSincronia(null, null, 0, null);
    sesionDeChatRef.current += 1;
    creatingChatRef.current = false;
    try {
      sessionStorage.removeItem(claves.mensajes);
      sessionStorage.removeItem(claves.chatId);
      sessionStorage.removeItem('atlas-v2-ephemeral-project-id');
      projectIdRef.current = getEphemeralProjectId();
    } catch {
      /* noop */
    }
  }, [soltarDocumentosDelChat, fijarSincronia]);

  return {
    messages,
    streaming,
    toolActivity,
    activeToolCard,
    batchSize,
    activeIndex,
    sendMessage,
    attachments,
    addAttachment,
    removeAttachment,
    clearAttachments,
    reemplazarAdjuntos,
    getReferenceImages,
    getReferenciasEtiquetadas,
    preferenciasDeGeneracion,
    recordarPreferencia,
    noPreguntarMas,
    setReferenceImages,
    setOutputKindHint,
    setModel,
    setResearchEnabled,
    resolveToolCard,
    patchToolCard: updateToolCard,
    getChatId,
    llegarAlEstudio,
    sesionDeChat,
    loadChatById,
    addChatCredits,
    fetchConsumo,
    stopTurn,
    detachTurn,
    reset,
    pushUserMessage,
    pushAssistantText,
    pushToolCard,
  };
};
